import { StatusDocumentUpload } from '@/components/define-document-upload/define-document-upload.config';
import {
	AgriFranchiseEnum,
	AnyObject,
	ApplicantTypeEnum,
	AppProgramStatusEnum,
	AreaRefTableNameEnum,
	AssignmentFranchiseTypeEnum,
	BusinessType,
	CaroEnum,
	CommonYearRegisterListEnum,
	ContentProcessInfoEnum,
	DivisionRecommenderEnum,
	DocumentObjectListRefTableEnum,
	DocumentObjectListSearchTypeEnum,
	DocumentTypeEnum,
	EAppTypeEnum,
	EDashboardStatisticEnum,
	EDeviceEnum,
	EmploymentTypeEnum,
	EStatusInquiryType,
	EUpdateAreaEnum,
	ExtensionStayEnum,
	FileTypeEnum,
	FisheryFranchiseEnum,
	GradeEnum,
	Infrastructure,
	IsAllocationRestrictedEnum,
	National,
	Ownership,
	PageSize,
	PageTypeEnum,
	PageYearListEnum,
	ProductionTypeEnum,
	ProductionUnitEnum,
	ReEntryRecommendationStatusEnum,
	ResidenceEnum,
	RoleEnum,
	ServerMessageCode,
	SettingUnitMasterInfoFieldTypeEnum,
	SettingUnitMasterInfoObjectEnum,
	SettingUnitMasterInfoStatusEnum,
	TypeUpdateSettingMasterInfoEnum,
	UnitTypeEnum,
	UpdateFieldNameEnum,
	UpdateHistoryRefTableNameEnum,
	UserSettingScreenCodeEnum,
	UserSettingTypeEnum,
	VisaTimeEnum,
} from '@/constant';
import { IWorkerLodgingApplicantDetailStore } from '@/stores';

import { AxiosError, AxiosResponse } from 'axios';

//#region COMMON TYPE

export type TCommonGetFieldsBody = {
	ids: number[];
	table: string;
	fields: string[];
};

export type TCommonGetFieldsResponse = {
	ID: number;
	[key: string]: any;
}[];

export type TCommonDistrict = {
	id: number;
	unitId: number;
	name: string;
	registId: number;
	registName: string;
	registDt: string; // ISOString
};

//TODO: type of unit, program, etc.
export type TGetCommonListResponse =
	| string[]
	| TSystemUnitItem[]
	| TCommonDistrict[]
	| IProgram[]
	| IFranchiseItem[]
	| IAssignmentItem[]
	| ISelectApplicantPopup[]
	| IWorkerProviderLodgingItem[]
	| IAutocompleteSuggestionItem[]
	| ICommonApplicant[]
	| ICommonLodging[]
	| ICommonRecommender[]
	| IWCFranchiseAssignmentItem[]
	| IFranchiseApplicantToAssignedItem[]
	| IFranchiseAssignment[]
	| ICommonAssignmentApplicant[]
	| GetDistrictValidatorResponse[];

export interface ICommonNotice {
	id: number;
	title: string;
	content: string;
	contentOnlyText: string;
	targetProgramId: string | number;
	targetDistrictId: string | number;
	targetAppType: EAppTypeEnum;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	files: TGetDocumentDetailResponse[];
	unitId: number;
	unitName: string;
	targetUnitName: string;
}

export type GetCommonNoticeListResponse = TGetDataList<ICommonNotice>;

export interface GetCommonNoticeListParams extends TGetDataSearchFilter {}

export type TCreateCommonNoticeRequest = {
	title: string;
	content: string;
	targetProgramId?: string | number;
	targetDistrictId?: string | number;
	targetAppType?: string | null; // 'FRANCHISE' | 'APPLICANT' | 'ALL'
	attachFileIds?: number[];
	contentFileIds?: number[];
};

export interface ICommonInquiryBoard {
	id: number;
	unitId: number;
	targetUnitId: number;
	targetUnitName: string;
	title: string;
	type: string;
	contents: string;
	answer: string;
	answerUserId: number;
	answerUserName: string;
	answerRegistDt: string;
	answerUpdateDt: string;
	answerFiles: TGetDocumentDetailResponse[];
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	files: TGetDocumentDetailResponse[];
	unitName: string;
	status: EStatusInquiryType;
	contentsOnlyText: string;
	device: EDeviceEnum;
	inquiryBoardId: number;
	registApplicantId: number;
	registFranchiseId: number;
}

export type GetCommonInquiryBoardListResponse =
	TGetDataList<ICommonInquiryBoard>;

export interface GetCommonInquiryBoardListParams extends TGetDataSearchFilter {
	title?: string | null;
	status?: string | null;
}

export type TCreateCommonInquiryBoardRequest = {
	type: string;
	title: string;
	contents: string;
	targetUnitId: number | null;
	attachFileIds: number[];
	contentFileIds?: number[];
};

export type TSaveAnswerCommonInquiryBoard = {
	id: number;
	answer: string;
	attachFileIds: number[];
};

//#endregion

export enum ApiResponseEnum {
	SUCCESS = 'SUCCESS',
	ERROR = 'ERROR',
}

export type ApiResponseError = Required<
	AxiosError<{
		timestamp: string;
		status: number;
		message: string;
		path: string;
		action: string;
		messageCode: ServerMessageCode;
		errorData: {
			fieldNames: string[];
			errorIds?: number[];
		};
	}>
>;

export type ApiLoginResponseSuccess = {
	data: string; // accessToken (when typeAccess is not EXTERNAL)
	typeAccess?: 'INTERNAL' | 'EXTERNAL';
	// Fields for EXTERNAL access (OTP required)
	preAuthToken?: string;
	maskedEmail?: string;
	expiresAt?: number;
};

export type ApiOauthUrlResponseSuccess = {
	data: string;
};

export type ApiOAuthTokenResponseSuccess = {
	logoutUrl: string;
	accessToken: string;
};

export type ApiResponseSuccess<T> = AxiosResponse<T> & { total?: number };

export type TGetDataList<T> = {
	content: T[];
	total: number;
	page: number;
	pageSize: PageSize;
	searchTypes?: GetListSearchTypes['searchTypes'];
	allData?: boolean;
};

export interface GetListSearchTypes {
	searchTypes?: Array<{
		searchType: string;
		searchValue?: string | number | null;
		searchValueIn?: Array<string>;
		searchBoolean?: boolean | null;
		startDate?: string | null;
		endDate?: string | null;
		startTime?: string | null;
		endTime?: string | null;
		startDateTime?: string | null;
		endDateTime?: string | null;
	}>;
}
export interface GetListPageParams {
	page: number;
	pageSize: PageSize;
}

export type TGetDataSearchFilter = GetListPageParams &
	GetListSearchTypes & {
		allData?: boolean;
		exportExcel?: boolean;
		sortSql?: string;
		downloadReason?: string;
		downloadFileName?: string;
	};

export type TGetDataListsDetail = GetListPageParams;

export type TGetListUserSettingRequest = {
	screenCode: UserSettingScreenCodeEnum;
};

export type TGetListUserSettingResponse = {
	objectName: PageTypeEnum;
	fields: string[];
}[];

export type TUserSettingBody = {
	type: UserSettingTypeEnum;
	subType: UserSettingScreenCodeEnum;

	/** @description content is JSON.stringify({objectName: PageTypeEnum, fields: string[]}) */
	content: string;
};

export type TUpdateField = {
	name: keyof typeof UpdateFieldNameEnum;
	value: string | null;
};
export type TUpdateFieldsRequest = {
	objectName: string;
	id: string | number;
	updateFields: TUpdateField[];
};

export type TUpdateFieldResponse = {
	fieldEnum: keyof typeof UpdateFieldNameEnum;
	name: string;
	value: string | null;
};

export type TUpdateFieldsResponse = {
	fields: TUpdateFieldResponse[];
};

export type TPayloadUpdateSuccess<T> = {
	data: {
		[key in keyof T]?: string | number;
	};
};

export type GetUpdateHistoryListParams = {
	refTableName: UpdateHistoryRefTableNameEnum;
	refTableId: number;
	/**
	 * @description fieldName camelCase basic-info of screen
	 */
	fieldName?: string;
	fieldArea?: string;
} & Omit<TGetDataSearchFilter, 'searchTypes'>;

export interface IUpdateHistory {
	id: number;
	fieldName: string;
	dataType: string;
	prevData: string | AnyObject;
	afterData: string | AnyObject;
	refTableName: UpdateHistoryRefTableNameEnum;
	refTableId: number;
	registId: number;
	registName: string;
	registDt: string;
	fieldArea: string;
}

export type GetUpdateHistoryListResponse = TGetDataList<IUpdateHistory>;

export type LoginParams = {
	email: string;
	password: string;
	rememberMe?: boolean;
};

// OTP Types
export type OtpSendParams = {
	preAuthToken: string;
};

export type OtpSendResponse = {
	preAuthToken: string;
	maskedEmail: string;
	expiresAt: number; // timestamp in milliseconds
};

export type OtpVerifyParams = {
	preAuthToken: string;
	otpValue: string;
};

// OtpVerifyResponse is just the accessToken string directly
export type OtpVerifyResponse = string;

export interface IProgramParam {
	unitId?: string;
	requestAll?: boolean;
	programId?: string;
	applicantId?: string;
	isAssigned?: string;
}

export type IProgram = {
	id: number;
	unitId: number;
	name: string;
	startDate: string;
	endDate: string;
	memo: string;
	farmCnt: number;
	argriculturalCoCnt: number;
	publicFranchiseCnt: number;
	fishingCnt: number;
	corporateFishingCnt: number;
	publicFishingCnt: number;
	franchiseCnt: number;
	mouApplicantCnt: number;
	marriageImmigrantsCnt: number;
	freignersStayingCnt: number;
	reEntryCnt: number;
	applicantCnt: number;
	extensionStayCnt: number;
	workplaceChangeCnt: number;
	unauthorizedDepartureCnt: number;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	registType: string;
};

export type IWorkerProgram = {
	programId: number;
	programName: string;
	programStartDate: string;
	programEndDate: string;
	programFarmCnt: number;
	programAgriculturalCoCnt: number;
	programPublicFranchiseCnt: number;
	programFishingCnt: number;
	programCorporateFishingCnt: number;
	programFranchiseCnt: number;
	programMouApplicantCnt: number;
	programMarriageImmigrantsCnt: number;
	programForeignersStayingCnt: number;
	programReEntryCnt: number;
	programApplicantCnt: number;
	programWorkplaceChangeCnt: number;
	programUnauthorizedDepartureCnt: number;
	programRegistDt: string;
	programUpdateDt: string;
	programExtensionStayCnt: number;
};

export type GetWorkerProgramListResponse = TGetDataList<IWorkerProgram>;
export type GetWorkerProgramListParams = TGetDataSearchFilter & {
	name?: string | null;
	programStartDate?: string | null;
	programEndDate?: string | null;
	registStartDate?: string | null;
	registEndDate?: string | null;
	allData?: boolean;
};

export type IWorkerProgramStatus = {
	programId: number;
	programName: string;
	programStartDate: string;
	programEndDate: string;
	programFarmCnt: number;
	programAgriculturalCoCnt: number;
	programPublicFranchiseCnt: number;
	programFishingCnt: number;
	programCorporateFishingCnt: number;
	programFranchiseCnt: number;
	programMouApplicantCnt: number;
	programMarriageImmigrantsCnt: number;
	programForeignersStayingCnt: number;
	programReEntryCnt: number;
	programApplicantCnt: number;
	programWorkplaceChangeCnt: number;
	programUnauthorizedDepartureCnt: number;
	programRegistDt: string;
	programUpdateDt: string;
	programExtensionStayCnt: number;
};

export type GetWorkerProgramStatusListResponse =
	TGetDataList<IWorkerProgramStatus>;

export type GetWorkerProgramStatusListParams = TGetDataSearchFilter & {
	name?: string | null;
	programStartDate?: string | null;
	programEndDate?: string | null;
	registStartDate?: string | null;
	registEndDate?: string | null;
	allData?: boolean;
};

export type GetWorkerProgramDetailResponse = IProgram;

export type TCreateWorkerProgramRequest = {
	name: string;
	startDate: string;
	endDate: string;
	memo: string;
};

export type IProgramApplicant = {
	id: number;
	programId: number;
	applicantId: number;
	birthDate: string;
	applicantName: string;
	programName: string;
	startDate: string;
	endDate: string;
	applicantType: ApplicantTypeEnum;
	isReEntry: boolean;
	isPublic: boolean;
	entryDate: string;
	extensionStayDate: string;
	expirationDate: string;
	departureDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	registDt: string;
	extensionPeriod: ExtensionStayEnum;
	extensionStayId: number;
	unauthorizedDepartureId: number;
	changeWorkPlaceId: number;
	reentryRecommendationStatus: ReEntryRecommendationStatusEnum;
	visaCode: ResidenceEnum;
	visaTime: VisaTimeEnum;
};
export type GetWorkerProgramApplicantListResponse = Omit<
	TGetDataList<IProgramApplicant>,
	'searchTypes'
>;
export type GetWorkerProgramApplicantListParams = GetListPageParams & {
	name: string;
};
export type GetWorkerProgramApplicantDetailResponse = IProgramApplicant;

export type TCreateWorkerProgramApplicantRequest = {
	programId: number;
	applicantIds: number[];
};

export type IProgramFranchise = {
	id: number;
	franchiseId: number;
	programId: number;
	type: BusinessType;
	typeDetail: AgriFranchiseEnum & FisheryFranchiseEnum;
	employerName: string;
	employerPhone: string;
	address: string;
	productionType: ProductionTypeEnum;
	registDt: string;
};
export type GetWorkerProgramFranchiseListResponse = Omit<
	TGetDataList<IProgramFranchise>,
	'searchTypes'
>;
export type GetWorkerProgramFranchiseListParams = TGetDataSearchFilter & {
	employerName: string;
	typeDetail?: string;
	type?: string;
};
export type GetWorkerProgramFranchiseDetailResponse = IProgramFranchise;

export type TCreateWorkerProgramFranchiseRequest = {
	programId: number;
	franchiseIds: number[];
};

export type GetWorkerProgramApplicantStatusResponse = Array<{
	programApplicantCnt: number;
	programExtensionStayCnt: number;
	programForeignersStayingCnt: number;
	programMarriageImmigrantsCnt: number;
	programMouApplicantCnt: number;
	programReEntryCnt: number;
	programUnauthorizedDepartureCnt: number;
	programWorkplaceChangeCnt: number;
	programId: number;
}>;

export type GetWorkerProgramFranchiseStatusResponse = Array<{
	programFarmCnt: number;
	programAgriculturalCoCnt: number;
	programPublicFranchiseCnt: number;
	programFishingCnt: number;
	programCorporateFishingCnt: number;
	programFranchiseCnt: number;

	programFarmApplicantCnt: number;
	programAgriculturalCoApplicantCnt: number;
	programOtherCnt: number;
	programFishingApplicantCnt: number;
	programCorporateFishingApplicantCnt: number;
	programOtherApplicantCnt: number;
	programId: number;
}>;

export type TSystemUnitItem = {
	id: number;
	name: string;
	type: string;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	addressDetail: string;
	subDomain: string;
	email: string;
	managerName: string;
	managerPhone: string;
	namePlateTitle: string;
	namePlateContent: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	cityProvince: string;
	countyDistrict: string;
	userId: number;
	isActivatedApplicantNoti: boolean;
	isActivatedFranchiseNoti: boolean;
	isActivatedExpApplicantNoti: boolean;
	isActivatedExpApplicantSms: boolean;
	isActive: boolean;
};

export type TGetSystemUnitListResponse = TGetDataList<TSystemUnitItem>;
export type TGetListSystemUnitParams = TGetDataSearchFilter & {
	name?: string | null;
};

export type TCreateSystemUnitRequest = {
	type: string;
	name: string;
	cityProvince: string;
	countyDistrict: string;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	addressDetail: string;
	subDomain: string;
	managerName: string;
	managerPhone: string;
	email: string;
	password: string;
};

export interface ISystemUser {
	id: number;
	unitId: number;
	email: string;
	departmentName: string;
	name: string;
	phone: string;
	role: number;
	roleEnum: RoleEnum;
	publicMainFranchiseId: number;
	districtId: number;
	lastestLoggedInDt: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
}

export type GetSystemUserListResponse = TGetDataList<ISystemUser>;
export interface GetSystemUserListParams extends TGetDataSearchFilter {
	name?: string | null;
	email?: string | null;
}

export type TCreateSystemUserRequest = {
	departmentName: string;
	name: string;
	email: string;
	password: string;
	phone: string;
};

export type TDataMyPage = {
	id: number;
	unitId: number;
	unitName: string;
	unitManagerName: string;
	unitCompanySealFilePath: string;
	email: string;
	departmentName: string;
	name: string;
	phone: string;
	role: RoleEnum;
	publicMainFranchiseId: number;
	districtId: number;
	districtName: string;
	lastestLoggedInDt: string;
	registId: number;
	registName: string;
	registDt: string;
};
export type TGetHistoryUpdateSystemUnitListParams = GetListPageParams & {
	unitId: string;
	field: string;
};

export type TSystemHistoryUpdateUnitItem = {
	id: number;
	fieldName: string;
	title: string;
	prevData: string;
	afterData: string;
	registName: string;
	registDt: string;
};

export type GetSystemHistoryUpdateUnitListResponse =
	TGetDataList<TSystemHistoryUpdateUnitItem>;

export interface IWorkerUser {
	id: number;
	unitId: number;
	email: string;
	departmentName: string;
	name: string;
	phone: string;
	role: RoleEnum;
	publicMainFranchiseId?: number;
	districtId: number;
	lastestLoggedInDt: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
}

export type GetWorkerUserListResponse = TGetDataList<IWorkerUser>;
export interface GetWorkerUserListParams extends TGetDataSearchFilter {
	name?: string | null;
	districtId?: number | null;
}

export type TCreateWorkerUserRequest = {
	departmentName: string;
	name: string;
	email: string;
	password: string;
	phone: string;
	role: RoleEnum;
	districtId: number;
	publicMainFranchiseId: number;
};

export interface IForeignUser {
	id: number;
	email: string;
	name: string;
	phone: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
}

export type GetForeignUserListResponse = TGetDataList<IForeignUser>;
export interface GetForeignUserListParams extends TGetDataSearchFilter {}

export type TCreateForeignUserRequest = {
	name: string;
	email: string;
	password: string;
	phone: string;
};

export interface IForeignApplicant {
	id: number;
	unitId: number;
	districtId: number;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	gender: string;
	height: string;
	weight: string;
	phone: string;
	hometownAddr: string;
	hometownPhone: string;
	nationalCode: string;
	passportNo: string;
	passportIssueDate: string;
	passportExpireDate: string;
	applicationType: string;
	isReEntry: boolean;
	reEntryCount: number | null;
	isPublic: boolean;
	publicFranchiseId: number;
	grade: string;
	statusId: number;
	issuanceStatusId: number;
	visaIssuanceConfirmNo: string;
	reentryRecommendationStatus: ReEntryRecommendationStatusEnum;
	visaNo: string;
	foreignerNo: string;
	entryDate: string;
	departureDate: string;
	expirationDate: string;
	visaCode: string;
	memo: string;
	avatarId: number;
	registDt: string;
	updateDt: string;
	avatar: TGetDocumentDetailResponse[];
	avatarFile: string;
	registUnitName: string;
}
export type GetForeignApplicantDetailResponse = IForeignApplicant;

export type GetForeignApplicantListResponse =
	TGetDataList<IForeignApplicantListItem>;
export interface GetForeignApplicantListParams extends TGetDataSearchFilter {}

export interface GetForeignApplicantListsDetailParams
	extends TGetDataListsDetail {
	allData?: boolean;
	id: number;
}

export type TCreateForeignApplicantRequest = {
	name: string;
	email: string;
	password: string;
	phone: string;
};

export type GetForeignApplicantProgramListResponse =
	TGetDataList<TApplicantProgramItem>;

export type GetWorkerApplicantListResponse = TGetDataList<IApplicantListItem>;

export type GetWorkerApplicantDetailResponse = TApplicantItem;

export type GetWorkerApplicantAssignmentListResponse =
	TGetDataList<TApplicantAssignmentItem>;

export type GetWorkerApplicantProgramListResponse =
	TGetDataList<TApplicantProgramItem>;

export type GetWorkerApplicantProcessInfoListResponse =
	TGetDataList<TApplicantProcessInfoItem>;
export interface GetWorkerApplicantListParams extends TGetDataSearchFilter {
	ids?: number[];
}

export interface GetWorkerApplicantListsDetailParams
	extends TGetDataListsDetail {
	allData?: boolean;
	id: number;
	franchiseId?: number;
	programId?: number;
	type?: string;
}

export interface GetWorkerApplicantListsDetailAssignmentParams
	extends TGetDataListsDetail {
	allData?: boolean;
	id: number;
	franchiseId?: number;
	programId?: number;
}

export interface GetWorkerApplicantListsDetailProgramParams
	extends TGetDataListsDetail {
	allData?: boolean;
	id: number;
}

export interface GetWorkerApplicantListsDetailProcessInfoParams
	extends TGetDataListsDetail {
	allData?: boolean;
	id: number;
	type?: string;
}

export type TCreateWorkerApplicantLodgingBody = {
	id?: number;
	lodgingRelationship?: string;
	lodgingId?: number;
	lodgingAddress?: string;
	lodgingProviderName?: string;
	lodgingProviderPhone?: string;
	lodgingArrangementPending?: string;
	providerId?: number;
	providerTable?: string;
};

export type TGetWorkerApplicantDepartureDateBody = {
	entryDate: string | null;
	visaCode: ResidenceEnum | null;
	visaTime: VisaTimeEnum | null;
	extensionPeriod: string | null;
	departureDate?: string | null;
	isContextVisaCode?: boolean;
};

export type TUpdateDataMyPageRequest = {
	phone: string;
	currentPassword: string;
	newPassword: string;
};

export interface IApplicantCustomField {
	fieldNo: string;
	name: string;
	type: string;
	value: string;
}

export type TApplicantCustomField = {
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
};

export type TApplicantItem = {
	id: number;
	unitId: number;
	districtId: number;
	districtName: string;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	gender: string;
	height: string;
	weight: string;
	phone: string;
	hometownAddr: string;
	hometownPhone: string;
	nationalCode: string;
	passportNo: string;
	passportIssueDate: string;
	passportExpireDate: string;
	applicationType: string;
	isReEntry: boolean;
	reEntryCount: number | null;
	isPublic: boolean;
	publicFranchiseId: number | null;
	grade: string;
	statusId: number;
	issuanceStatusId: number;
	visaIssuanceConfirmNo: string;
	reentryRecommendationStatus: ReEntryRecommendationStatusEnum;
	visaNo: string;
	foreignerNo: string;
	entryDate: string;
	departureDate: string;
	expirationDate: string;
	extensionPeriod: ExtensionStayEnum;
	visaCode: ResidenceEnum;
	visaTime: VisaTimeEnum;
	memo: string;
	avatarId: number;
	registDt: string;
	updateDt: string;
	avatar: TGetDocumentDetailResponse[];
	assignmentApplicantCount: number;
	customField: IApplicantCustomField[];
	avatarFile: string;
	recommenderRelationship: string;
	recommenderRelationshipForIssueApplicationDownload: string;
	isWithdrawn?: boolean;
	registUnitName: string;
	midTermDeparture: string;
};

export interface IApplicantCards {
	id: number;
	programName: string;
	fullName: string;
	avatarUrl: string;
	national: string;
	franchiseName: string;
}

export interface IApplicantNameplate {
	unitName: string;
	namePlateContent: string;
	applicantCards: IApplicantCards[];
}

export interface IApplicantListItem {
	applicantPassportExpireDate: string | null;
	applicantGrade: string;
	applicantDistrictId: string;
	applicantDistrictName: string;
	applicantWeight: string;
	applicantFullName: string;
	applicantHeight: string;
	applicantReEntryCount: string;
	applicantUpdateDt: string;
	applicantEntryDate: string | null;
	applicantHometownPhone: string;
	applicantPassportNo: string;
	applicantStatusId: number | string;
	applicantStatusName: string;
	applicantRegistDt: string;
	applicantVisaIssuanceConfirmNo: string;
	applicantUnauthorizedDepartureDate: string | null;
	applicantMemo: string;
	applicantId: number;
	applicantVisaCode: string;
	applicantChangeWorkDate: string | null;
	applicantHometownAddr: string;
	applicantBirthDate: string | null;
	applicantVisaNo: string;
	applicantLastName: string;
	applicantNationalCode: string;
	applicantIsReEntry: boolean | CaroEnum | number | string;
	applicantFirstName: string;
	applicantEmail: string;
	applicantPublicFranchiseId: number | string;
	applicantPublicFranchiseName: string;
	applicantDepartureDate: string | null;
	applicantGender: string;
	applicantIssuanceStatusId: string;
	applicantIssuanceStatusName: string;
	applicantExpirationDate: string | null;
	applicantPassportIssueDate: string | null;
	applicantApplicationType: string;
	applicantIsPublic: boolean | CaroEnum | number | string;
	applicantPhone: string;
	applicantForeignerNo: string;
	applicantExtensionStayDate: string | null;
	applicantCustomField1: string;
	applicantCustomField2: string;
	applicantCustomField3: string;
	applicantCustomField4: string;
	applicantCustomField5: string;
	applicantLodgingAddress: string;
	applicantReentryRecommendationStatus: ReEntryRecommendationStatusEnum;
	applicantExtensionPeriod: ExtensionStayEnum;
	programName: string;
	fullName: string;
	avatarUrl: string;
	national: string;
	applicantVisaTime: VisaTimeEnum;
	applicantIsWithdrawn: string;
	applicantAgreementDt: string | null;
	applicantMidTermDeparture: string;
	departureDate: string;
	entryDate: string;
	visaCode: ResidenceEnum;
	visaTime: VisaTimeEnum;
	extensionPeriod: ExtensionStayEnum;
}

export interface IForeignApplicantListItem {
	foreignApplicantPassportExpireDate: string | null;
	foreignApplicantDistrictId: string;
	foreignApplicantDistrictName: string;
	foreignApplicantWeight: string;
	foreignApplicantFullName: string;
	foreignApplicantHeight: string;
	foreignApplicantUpdateDt: string;
	foreignApplicantEntryDate: string | null;
	foreignApplicantHometownPhone: string;
	foreignApplicantPassportNo: string;
	foreignApplicantStatusId: number | string;
	foreignApplicantStatusName: string;
	foreignApplicantRegistDt: string;
	foreignApplicantId: number;
	foreignApplicantHometownAddr: string;
	foreignApplicantBirthDate: string | null;
	foreignApplicantLastName: string;
	foreignApplicantNationalCode: string;
	foreignApplicantFirstName: string;
	foreignApplicantEmail: string;
	foreignApplicantGender: string;
	foreignApplicantPassportIssueDate: string | null;
	foreignApplicantApplicationType: string;
	foreignApplicantPhone: string;
	programName: string;
	fullName: string;
	avatarUrl: string;
	national: string;
}

export type TCreateWorkerApplicantRequest = TApplicantItem & {
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	avatarId: string;
};

export type TUpdateStatusWorkerApplicantRequest = {
	applicantId: number;
	statusId: number;
	status: string;
};

export type TUpdateExtensionPeriodRequest = {
	applicantIds: number[];
	extensionPeriod: ExtensionStayEnum | null;
};

export type TApplicantAssignmentItem = {
	id: number;
	assignmentFranchiseId: number;
	franchiseId: number;
	franchiseName: string;
	franchisePhone: string;
	address: string;
	workStartDate: string;
	workEndDate: string;
	status: string;
	memo: string;
	type: AssignmentFranchiseTypeEnum;
	franchiseEmployerName: string;
	startDate: string;
	endDate: string;
	publicFranchiseId: number;
	evaluationScore: number;
	publicStatus: string;
};

export type TApplicantProgramItem = {
	id: number;
	programId: number;
	applicantId: number;
	birthDate: string;
	applicantName: string;
	programName: string;
	startDate: string;
	endDate: string;
	applicantType: string;
	isReEntry: boolean;
	isPublic: boolean;
	entryDate: string;
	extensionStayDate: string;
	expirationDate: string;
	departureDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	registDt: string;
	reentryRecommendationStatus: ReEntryRecommendationStatusEnum;
	status: AppProgramStatusEnum;
	visaTime: VisaTimeEnum;
	visaCode: ResidenceEnum;
	extensionPeriod: ExtensionStayEnum;
};

export type TApplicantProcessInfoItem = {
	id: number;
	applicantId: number;
	franchiseId: number;
	type: ContentProcessInfoEnum;
	referId: number;
	processingDate: string;
	registId: number;
	registName: string;
	registDt: string;
	franchiseName: string;
	franchisePhone: string;
	deleteId: number;
};

export type TApplicantRecommender = {
	id: number;
	recommenderId: number;
	recommenderRelationship: string;
	type: string;
	name: string;
	engName: string;
	providerNationalCode: string;
	providerNationalName: string;
	visaCode: string;
	address: string;
	birthDate: string;
	phone: string;
	gender: string;
};

export type TApplicantLodging = {
	lodgingId: number;
	lodgingArrangementPending: string;
	lodgingRelationship: string;
	providerName: string;
	providerPhone: string;
	lodgingAddress: string;
	facilityType: Infrastructure;
};

export type TLodgingAutoFillList = {
	lodgingId: number;
	lodgingArrangementPending: string;
	lodgingRelationship: string;
	providerName: string;
	providerPhone: string;
	lodgingAddress: string;
};

export type TApplicantLodgingAutoFill = {
	franchiseId: number;
	franchiseName: string;
	franchiseAddress: string;
	franchisePhone: string;
	lodgingList: TLodgingAutoFillList[];
};

export type TUploadExcelApplicantItem = {
	district: string;
	lastName: string;
	firstName: string;
	birthDate: string;
	email: string;
	gender: string;
	height: string;
	weight: string;
	phone: string;
	hometownAddr: string;
	hometownPhone: string;
	nationalCode: string;
	passportNo: string;
	passportIssueDate: string;
	passportExpireDate: string;
	applicationType: string;
	isReEntry: string;
	reEntryCount: string;
	isPublic: string;
	publicFranchiseName: string;
	grade: string;
	status: string;
	issuanceStatus: string;
	visaIssuanceConfirmNo: string;
	visaNo: string;
	foreignerNo: string;
	entryDate: string;
	//TODO: check with BE
	extensionDate: string;
	expirationDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	departureDate: string;
	visaCode: string;
	memo: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	departureDateDDay: string;
	extensionPeriod: string;
	visaTime: string;
	midTermDeparture: string;
};

export type TUploadExcelApplicantFailedItem = {
	data: TUploadExcelApplicantItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelApplicantResponse = TUploadExcelApplicantFailedItem[];

export type TUploadExcelLodgingItem = {
	id?: string;
	district: string;
	address: string;
	ownershipType: string;
	facilityType: string;
	lodgingAssignCnt: string;
	providedDate: string;
	inspectionDate: string;
	providerType: string;
	providerName: string;
	providerNationalCode: string;
	providerPhone: string;
	providerAddress: string;
	memo: string;
	providerTable: string;
	lodgingCost: string;
	mealCost: string;
};

export type TUploadExcelLodgingFailedItem = {
	data: TUploadExcelLodgingItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelLodgingResponse = TUploadExcelLodgingFailedItem[];

export interface IFranchiseCustomField {
	dataType: SettingUnitMasterInfoFieldTypeEnum;
	fieldNo: number;
	name: string;
	value: string;
}
//TODO: update this when has api
export interface IFranchiseItem {
	id: number;
	unitId: number;
	email: string;
	districtId: number;
	name: string;
	phone: string;
	type: BusinessType;
	typeDetail: FisheryFranchiseEnum & AgriFranchiseEnum;
	productionType: ProductionTypeEnum;
	productionTypeDetail: string;
	productionUnit: ProductionUnitEnum;
	productionScale: string;
	address: string;
	employerName: string;
	employerBirthDate: string;
	employerPhone: string;
	employmentType: EmploymentTypeEnum;
	isPublic: boolean;
	publicFranchiseId: number;
	businessRegistNo: string;
	corporateRegistNo: string;
	penaltyPointsAccumulated: string;
	isAllocationRestricted: number;
	memo: string;
	workStartTime: string;
	workEndTime: string;
	monthlyWorkingHours: number;
	dailyBreaksNumber: number;
	dailyBreaksHour: number;
	dailyBreaksMin: number;
	requestApplicant: number;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	isLack: number;
	hasLodging: number;
	gradeADailyWage: number | string;
	gradeBDailyWage: number | string;
	gradeCDailyWage: number | string;
	defaultGrade: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	customField?: Array<IFranchiseCustomField>;
	districtName: string;
	hasPrevYearAssignment: boolean;
	prevYearAssignmentCnt: number;
	lodgingAddress: string;
	disabled?: boolean;
	startDate?: string;
	endDate?: string;
	isWithdrawn?: boolean;
}
export interface IFranchiseListItem {
	franchiseId: number;
	franchiseEmail: string;
	franchiseDistrictId: number | string;
	franchiseName: string;
	franchisePhone: string;
	franchiseType: BusinessType | string;
	franchiseTypeDetail: (FisheryFranchiseEnum & AgriFranchiseEnum) | string;
	franchiseProductionType: ProductionTypeEnum | string;
	franchiseProductionTypeDetail: string;
	franchiseProductionUnit: ProductionUnitEnum | string;
	franchiseProductionScale: string;
	franchiseAddress: string;
	franchiseEmployerName: string;
	franchiseEmployerBirthDate: string;
	franchiseEmployerPhone: string;
	franchiseIsPublic: boolean | CaroEnum | number;
	franchisePublicFranchiseId: number | string;
	franchiseBusinessRegistNo: string;
	franchiseCorporateRegistNo: string;
	franchisePenaltyPointsAccumulated: string;
	franchiseIsAllocationRestricted:
		| boolean
		| IsAllocationRestrictedEnum
		| string
		| number;
	franchiseMemo: number;
	franchiseWorkStartTime: string;
	franchiseWorkEndTime: string;
	franchiseMonthlyWorkingHours: number;
	franchiseDailyBreaksNumber: number;
	franchiseDailyBreaksHour: number;
	franchiseDailyBreaksMin: number;
	franchiseRequestApplicant: number;
	assignmentFranchiseRequestApplicant: string;
	franchiseAssignmentApplicant: number;
	franchiseAdditionalAssignmentApplicant: number;
	franchiseIsLack: number;
	franchiseHasLodging: number | CaroEnum | string | number;
	franchiseGradeAHourlyWage: number;
	franchiseGradeBHourlyWage: number;
	franchiseGradeCHourlyWage: number;
	franchiseDefaultGrade: string;
	franchiseRegistId: number;
	franchiseRegistName: string;
	franchiseRegistDt: number | string;
	franchiseUpdateDt: number | string;
	franchiseCustomField1: string;
	franchiseCustomField2: string;
	franchiseCustomField3: string;
	franchiseCustomField4: string;
	franchiseCustomField5: string;
	franchisePublicFranchiseName?: string;
	franchiseDistrictName?: string;
	franchiseMaxManpowerAllowed?: number;
	franchiseAgreementDt: string | null;
}

type AddNewAfterFranchise<T> = {
	[K in keyof T as K extends `franchise${string}`
		? `franchiseNew${K extends `franchise${infer Rest}` ? Rest : never}`
		: K]: T[K];
};

export type IFranchiseNewListItem = AddNewAfterFranchise<IFranchiseListItem>;

export type GetWorkerFranchiseListResponse = TGetDataList<IFranchiseListItem>;
export type GetWorkerFranchiseDetailResponse = IFranchiseItem;
export type GetWorkerFranchiseListParams = TGetDataSearchFilter & {
	employerName?: string;
	districtId?: string;
	isWithoutInspectionDoc?: boolean;
};

export type TCreateWorkerFranchiseRequest = {
	email?: string;
	name: string;
	phone?: string;
	type?: BusinessType;
	typeDetail?: AgriFranchiseEnum & FisheryFranchiseEnum;
	productionType?: ProductionTypeEnum;
	productionTypeDetail?: string;
	productionUnit?: ProductionUnitEnum;
	productionScale?: string;
	districtId?: number;
	address?: string;

	employerName: string;
	employerBirthDate?: string;
	employerPhone?: string;
	isPublic?: boolean;
	publicFranchiseId?: number;
	businessRegistNo?: string;
	corporateRegistNo?: string;
	penaltyPointsAccumulated?: string;
	isAllocationRestricted?: boolean;

	memo?: string;

	workStartTime?: string;
	workEndTime?: string;
	monthlyWorkingHours?: number;
	dailyBreaksNumber?: number;
	dailyBreaksHour?: number;
	dailyBreaksMin?: number;

	gradeADailyWage?: number;
	gradeBDailyWage?: number;
	gradeCDailyWage?: number;
	defaultGrade?: GradeEnum;

	customField1?: string;
	customField2?: string;
	customField3?: string;
	customField4?: string;
	customField5?: string;
};

export type IFranchiseAssignment = {
	id: number;
	applicantId: number;
	fullName: string;
	birthDate: string;
	workStartDate: string;
	workEndDate: string;
	statusId: number;
	statusName: string;
	recommenderId: number;
	recommenderName: string;
	recommenderRelationship: DivisionRecommenderEnum;
	lodgingId: number;
	lodgingAddress: string;
	lodgingArrangementPending: string;
	memo: string;
	type: AssignmentFranchiseTypeEnum;
	assignmentApplicantId: number;
	assignmentFranchiseType: AssignmentFranchiseTypeEnum;
	districtId: number;
};
export type GetWorkerFranchiseAssignmentListResponse = Omit<
	TGetDataList<IFranchiseAssignment>,
	'searchTypes'
>;
export type GetWorkerFranchiseAssignmentListParams = GetListPageParams & {
	programId: string;
	applicantId: string;
	allData?: boolean;
};

export type IFranchiseLodging = {
	id: number;
	ownershipType: Ownership;
	providerName: string;
	providerPhone: string;
	facilityType: Infrastructure;
	address: string;
	residentCnt: number;
};
export type GetWorkerFranchiseLodgingListResponse = Omit<
	TGetDataList<IFranchiseLodging>,
	'searchTypes'
>;
export type GetWorkerFranchiseLodgingListParams = GetListPageParams;
export type TCreateWorkerFranchiseLodgingRequest = {
	ownershipType: Ownership;
	facilityType: Infrastructure;
	districtId: number;
	address: string;
	providedDate: string;
};

export type TFranchiseRecommenderInfoItem = {
	id: number;
	unitId: number;
	name: string;
	type: string;
	engName: string;
	providerNationalCode: string;
	visaCode: string;
	birthDate: string;
	phone: string;
	email: string;
	address: string;
	memo: string;
	spouseName: string;
	spousePhone: string;
	franchiseId: number;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	recommendedNumberPeople: number;
	franchiseName: string;
	subType: string;
};

export type GetWorkerFranchiseRecommenderInfoListResponse =
	TFranchiseRecommenderInfoItem[];
export type IFranchiseProgram = {
	id: number;
	name: string;
	status: AppProgramStatusEnum;
	registType: string;
	startDate: string;
	endDate: string;
	registDt: string;
};
export type GetWorkerFranchiseProgramListResponse = Omit<
	TGetDataList<IFranchiseProgram>,
	'searchTypes'
>;
export type GetWorkerFranchiseProgramListParams = GetListPageParams;
export type TCreateWorkerFranchiseProgramRequest = Array<{
	programId: number;
	franchiseIds: number[];
}>;

export type GetWorkerRecommenderListParams = TGetDataSearchFilter & {
	type?: DivisionRecommenderEnum | null;
	name?: string | null;
};

export interface ICommonRecommender {
	id: number;
	franchiseId: number;
	name: string;
	type: string;
	phone: string;
	spouseName: string;
	registDt: string;
}

export interface IWorkerRecommenderWhenRequestCreateSuccess {}

export interface IWorkerRecommenderItem {
	id: number;
	unitId: number;
	name: string;
	type: DivisionRecommenderEnum;
	engName: string;
	providerNationalCode: string;
	visaCode: string;
	birthDate: string;
	phone: string;
	email: string;
	address: string;
	memo: string;
	subType: string;
	spouseName: string;
	spousePhone: string;
	franchiseId: number;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	recommendedNumberPeople: number;
	franchiseName: string;
}

export interface IRecommenderListItem {
	recommenderId: number;
	recommenderUnitId: number;
	recommenderName: string;
	recommenderType: DivisionRecommenderEnum;
	recommenderEngName: string;
	recommenderProviderNationalCode: string;
	recommenderVisaCode: string;
	recommenderBirthDate: string;
	recommenderPhone: string;
	recommenderEmail: string;
	recommenderAddress: string;
	recommenderMemo: string;
	recommenderSpouseType: string;
	recommenderSpouseName: string;
	recommenderSpousePhone: string;
	recommenderFranchiseId: number;
	recommenderRegistId: number;
	recommenderRegistName: string;
	recommenderRegistDt: string;
	recommenderUpdateDt: string;
	recommenderRecommendedNumberPeople: number | string;
	recommenderFranchiseName: string;
}

export type GetWorkerRecommenderApplicantListParams = TGetDataSearchFilter & {
	id: number;
	programId?: number | null;
};

export interface IWorkerRecommenderApplicantItem {
	id: number;
	recommenderRelationship: string;
	name: string;
	birthDate: string;
	memo: string;
	applicantRecommenderRelationship: string;
	// Extended fields returned by /worker/recommender/{id}/applicant/list
	nationalCode?: string | null;
	gender?: string | null;
	passportNo?: string | null;
	hometownAddr?: string | null;
}

export type TCreateWorkerRecommenderBody = {
	type: string;
	name: string;
	engName: string;
	providerNationalCode: string;
	visaCode: string | null;
	birthDate: string;
	phone: string;
	email: string;
	address: string;
	memo: string;
	spouseName: string;
	spousePhone: string;
	franchiseId?: number | null;
};

export type TCreateWorkerLodgingBody = {
	address: string;
	districtId: number;
	ownershipType: Ownership;
	facilityType: Infrastructure;
	providedDate: string;
	inspectionDate: string;
	providerName: string;
	providerNationalCode: string;
	providerPhone: string;
	providerAddress: string;
	memo: string;
	providerId: number;
	providerTable: PageTypeEnum;
};

export type GetWorkerRecommenderListResponse =
	TGetDataList<IRecommenderListItem>;

export type GetWorkerRecommenderDetailResponse = IWorkerRecommenderItem;

export type GetWorkerRecommenderApplicantListResponse =
	TGetDataList<IWorkerRecommenderApplicantItem>;

export type GetWorkerRecommenderApplicantDetailResponse = {
	data: IWorkerRecommenderApplicantItem;
};

export type TCreateWorkerRecommenderApplicantBody = {
	applicantId: number;
	recommenderRelationship?: string;
};

export type GetWorkerRecommenderLodgingListResponse =
	TGetDataList<TRecommenderLodgingItem>;

export type TRecommenderLodgingItem = {
	id: number;
	ownershipType: Ownership;
	providerName: string;
	providerPhone: string;
	facilityType: string;
	address: string;
	residentCnt: number;
};

export interface IFranchiseApplicantToAssignedItem {
	id: number;
	unitId: number;
	email: string;
	districtId: number;
	name: string;
	phone: string;
	type: BusinessType;
	typeDetail: FisheryFranchiseEnum & AgriFranchiseEnum;
	productionType: ProductionTypeEnum;
	productionTypeDetail: string;
	productionUnit: ProductionUnitEnum;
	productionScale: string;
	address: string;
	employerName: string;
	employerBirthDate: string;
	employerPhone: string;
	isPublic: boolean;
	publicFranchiseId: number;
	businessRegistNo: string;
	corporateRegistNo: string;
	penaltyPointsAccumulated: string;
	isAllocationRestricted: number;
	memo: number;
	workStartTime: string;
	workEndTime: string;
	monthlyWorkingHours: number;
	dailyBreaksNumber: number;
	dailyBreaksHour: number;
	dailyBreaksMin: number;
	requestApplicant: number;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	isLack: number;
	hasLodging: number;
	gradeADailyWage: number | string;
	gradeBDailyWage: number | string;
	gradeCDailyWage: number | string;
	defaultGrade: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;

	customField?: Array<IFranchiseCustomField>;
	districtName: string;
}

export type TUploadExcelRecommenderItem = {
	id: string;
	type: string;
	name: string;
	engName: string;
	providerNational: string;
	visaCode: string;
	birthDateStr: string;
	phone: string;
	email: string;
	address: string;
	subType: string;
	spouseName: string;
	spousePhone: string;
	recommendedNumberPeople: string;
	memo: string;
};

export type TUploadExcelRecommenderFailedItem = {
	data: TUploadExcelRecommenderItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelRecommenderResponse =
	TUploadExcelRecommenderFailedItem[];

export type TExportExcelRecommenderResponse = Array<{
	id: string;
	unitId: string;
	name: string;
	type: string;
	engName: string;
	providerNationalCode: string;
	visaCode: string;
	visaCodeDisplay: string;
	birthDate: string;
	phone: string;
	email: string;
	address: string;
	memo: string;
	subType: string;
	spouseName: string;
	spousePhone: string;
	franchiseId: number;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	recommendedNumberPeople: number;
	franchiseName: string;
}>;

export type TUploadExcelAssignmentItem = {
	programName: string;
	no: number;
	applicantFirstName: string;
	applicantLastName: string;
	applicantBirthDate: string;
	applicantEmail: string;
	applicantMemo: string;
	franchiseName: string;
	franchiseEmail: string;
	startDate: string;
	endDate: string;
	requestApplicant: number;
	memo: string;
};

export type TUploadExcelAssignmentRequest = TUploadExcelAssignmentItem[];

export type TUploadExcelCurrentAssignmentItem = {
	applicantLastName: string;
	applicantFirstName: string;
	applicantBirthDate: string;
	applicantEmail: string;
	applicantId: number;
	assignmentApplicantMemo: string;

	franchiseName: string;
	franchiseEmail: string;
	franchiseId: number;
	assignmentFranchiseMemo: string;

	assignmentFranchiseStartDate: string;
	assignmentFranchiseEndDate: string;
	assignmentFranchiseRequestApplicant: number;

	assignmentFranchiseId: number;
};

export type TUploadExcelAssignmentFailedItem = {
	data: TUploadExcelAssignmentItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelAssignmentResponse = TUploadExcelAssignmentFailedItem[];

export type TUploadExcelPublicAssignmentItem = {
	programName: string;
	no: number;
	employerName: string;
	employerBirthDate: string;
	workingDate: string;
	startTime: string;
	endTime: string;
	relaxTime: string;
	requestApplicant: number;
	autoAssignment: string;
	numberOfMale: string;
	ageOfMale: string;
	numberOfFemale: string;
	ageOfFemale: string;
	applicantFirstName: string;
	applicantLastName: string;
	applicantBirthDate: string;
	memo: string;
};

export type TUploadExcelPublicAssignmentRequest =
	TUploadExcelPublicAssignmentItem[];

export type TUploadExcelCurrentPublicAssignmentItem = {
	applicantLastName: string;
	applicantFirstName: string;
	applicantBirthDate: string;
	applicantEmail: string;
	applicantId: number;
	assignmentApplicantMemo: string;

	franchiseName: string;
	franchiseEmail: string;
	franchiseId: number;
	assignmentFranchiseMemo: string;

	assignmentFranchiseStartDate: string;
	assignmentFranchiseEndDate: string;
	assignmentFranchiseRequestApplicant: number;

	assignmentFranchiseId: number;
};

export type TUploadExcelPublicAssignmentFailedItem = {
	data: TUploadExcelPublicAssignmentItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelPublicAssignmentResponse =
	TUploadExcelPublicAssignmentFailedItem[];

export type TUploadExcelFranchiseItem = {
	id: number;
	type: string;
	typeDetail: string;
	district: string;
	employerName: string;
	email: string;
	address: string;
	employerBirthDate: string;
	employerPhone: string;
	name: string;
	phone: string;
	productionType: string;
	productionTypeDetail: string;
	productionScale: string;
	productionUnit: string;
	maxManpowerAllowed: string;
	isPublic: string;
	publicFranchiseName: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	penaltyPointsAccumulated: string;
	isAllocationRestricted: boolean;
	workingTime: string;
	monthlyWorkingHours: string;
	dailyBreaksNumber: string;
	dailyBreaksHour: string;
	dailyBreaksMin: string;
	gradeADailyWage: string;
	gradeBDailyWage: string;
	gradeCDailyWage: string;
	defaultGrade: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	memo: string;
};

export type TUploadExcelFranchiseRequest = TUploadExcelFranchiseItem[];

export type TUploadExcelFranchiseFailedItem = {
	data: TUploadExcelFranchiseItem;
	errorList: Array<{
		field: string;
		reason: string;
	}>;
};

export type TUploadExcelFranchiseResponse = TUploadExcelFranchiseFailedItem[];

export interface IWorkerAssignmentItem {
	id: number;
	type: AssignmentFranchiseTypeEnum;
	programId: number;
	franchiseId: number;
	franchiseName: string;
	franchisePhone: string;
	franchiseTypeDetail: string;
	districtName: string;
	districtId: number;
	employerName: string;
	employerPhone: string;
	publicFranchiseId: number;
	address: string;
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	requestApplicant: number;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	memo: string;
	maxManpowerAllowed: number;
	registDt: string;
	updateDt: string;
	relaxTime?: number;
	applicantIds: number[];
}

export interface IWorkerAssignmentListItem
	extends IFranchiseListItem,
		IApplicantListItem,
		IRecommenderListItem,
		WorkerLodgingListItem {
	assignmentApplicantId: number;
	assignmentFranchiseId: number;
	assignmentFranchiseProgramId: number;
	assignmentFranchiseFranchiseId: number;

	assignmentFranchiseType: string;
	assignmentFranchiseRequestApplicant: string;
	assignmentFranchiseAssignmentApplicant: string;
	assignmentFranchiseAdditionalAssignmentApplicant: string;
	assignmentFranchiseStartDate: string;
	assignmentFranchiseEndDate: string;
	assignmentFranchiseMemo: string;
	assignmentFranchiseRegistId: string;
	assignmentFranchiseRegistName: string;
	assignmentFranchiseRegistDt: string;
	assignmentFranchiseUpdateDt: string;

	// otherFields
	applicantRecommenderRelationship: string;
}

export interface IWorkerAssignmentFranchiseListItem extends IFranchiseListItem {
	assignmentFranchiseId: number;
	assignmentFranchiseProgramId: number;
	assignmentFranchiseFranchiseId: number;

	assignmentFranchiseType: string;
	assignmentFranchiseRequestApplicant: string;
	assignmentFranchiseAssignmentApplicant: string;
	assignmentFranchiseAdditionalAssignmentApplicant: string;
	assignmentFranchiseStartDate: string;
	assignmentFranchiseEndDate: string;
	assignmentFranchiseMemo: string;
	assignmentFranchiseRegistId: string;
	assignmentFranchiseRegistName: string;
	assignmentFranchiseRegistDt: string;
	assignmentFranchiseUpdateDt: string;

	workingDateGroup: string;
}

export type GetWorkerAssignmentDetailResponse = IWorkerAssignmentItem;

export type GetWorkerAssignmentPublicDetailResponse = IWorkerAssignmentItem;

export type GetWorkerAssignmentListResponse =
	TGetDataList<IWorkerAssignmentListItem>;

export type GetWorkerAssignmentFranchiseListResponse =
	TGetDataList<IWorkerAssignmentFranchiseListItem>;

export interface GetWorkerAssignmentListParams extends TGetDataSearchFilter {
	ids?: number[];
	isLack?: number | boolean;
}

export type TDeleteWorkerAssignmentBody = Array<{
	assignmentFranchiseId: number;
	applicantId?: number;
}>;

export type GetCommonAssignmentApplicantListResponse =
	TGetDataList<ICommonApplicant>;

export type IWorkerAssignmentApplicantItem = {
	id: number;
	applicantId: number;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	workStartDate: string;
	workEndDate: string;
	workingHistory: string;
	statusId: number;
	statusName: string;
	entryDate: string;
	visaCode: ResidenceEnum;
	visaTime: VisaTimeEnum;
	extensionPeriod: ExtensionStayEnum;
	extensionStayDate: string;
	expirationDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	departureDate: string;
	age: number;
	recommenderName: string;
	providerName: string;
	memo: string;
	registDt: string;
	evaluationScore: number;
};

export type GetWorkerAssignmentApplicantListResponse =
	TGetDataList<IWorkerAssignmentApplicantItem>;

export type IWorkerAssignmentPublicApplicantItem = {
	id: number;
	applicantId: number;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	workStartDate: string;
	workEndDate: string;
	workingHistory: string;
	statusId: number;
	statusName: string;
	entryDate: string;
	visaCode: ResidenceEnum;
	visaTime: VisaTimeEnum;
	extensionPeriod: ExtensionStayEnum;
	extensionStayDate: string;
	expirationDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	departureDate: string;
	age: number;
	recommenderName: string;
	providerName: string;
	memo: string;
	registDt: string;
	evaluationScore: number;
	grade: string;
	height: number;
	weight: number;
	gender: string;
	salary: number;
};

export type GetWorkerAssignmentPublicApplicantListResponse =
	TGetDataList<IWorkerAssignmentPublicApplicantItem>;

export interface GetWorkerAssignmentApplicantListParams
	extends TGetDataSearchFilter {
	id: number;
	sortSql?: string;
}

export type TUpdateWorkerAssignmentApplicantStatusBody = {
	id: number;
	statusId: number;
}[];

export interface IWorkerAssignmentEvaluation {
	id: number;
	assignmentApplicantId: number;
	franchiseId: number;
	onTimeScore: number;
	attitudeScore: number;
	friendlyScore: number;
	responsibilityScore: number;
	evaluationScore: number;
	commentEvaluation: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
}

export interface IWorkerChangeWorkPlace
	extends IFranchiseListItem,
		IApplicantListItem,
		IFranchiseNewListItem {
	changeWorkPlaceId: string;
	changeWorkPlaceReason: string;
	changeWorkPlaceDetailReason: string;
	changeWorkPlaceAssignmentDate: string;
	changeWorkPlaceMemo: string;
	changeWorkPlaceSignificant: string;
	changeWorkPlaceRegistDt: string;
	changeWorkPlaceUpdateDt: string;
}

export interface IWorkerChangeWorkPlaceDetail {
	registDt: string;
	updateDt: string;
	id: number;
	programId: number | null;
	prevFranchiseId: number;
	newFranchiseId: number;
	prevAssignmentFranchiseId: number;
	newAssignmentFranchiseId: number;
	applicantId: number;
	assignmentDate: string;
	memo: string;
	reason: string;
	detailReason: string;
	prevEmployerName: string;
	prevEmployerBirthDate: string;
	prevEmployerPhone: string;
	prevEmail: string;
	prevAddress: string;
	prevBusinessRegistNo: string;
	prevWorkStartDate: string;
	prevWorkEndDate: string;
	prevRegistDt: string;
	newEmployerName: string;
	newEmployerBirthDate: string;
	newEmployerPhone: string;
	newEmail: string;
	newBusinessRegistNo: string;
	newStartDate: string;
	newEndDate: string;
	applicantName: string;
	applicationBirthDate: string;
	applicationStatusId: string | number;
	applicationStatusName: string;
	applicationVisaCode: string;
	newWorkStartDate: string;
	newWorkEndDate: string;
	newAddress: string;
	applicantForeignerNo: string;
	prevDistrictId: number | null;
	prevDistrictName: string;
	applicationDistrictId: number | null;
	applicationDistrictName: string;
	newDistrictId: number | null;
	newDistrictName: string;
}

export type GetWorkerChangeWorkPlaceListResponse =
	TGetDataList<IWorkerChangeWorkPlace>;

export interface GetWorkerChangeWorkPlaceListParams
	extends TGetDataSearchFilter {
	memo?: string | null;
	reason?: string;
	detailReason?: string;
	registStartDate?: string;
	registEndDate?: string;
	allData?: boolean;
}

export interface CreateWorkerChangeWorkPlaceRequest {
	prevAssignmentFranchiseId: number;
	applicantId: number;
	newFranchiseId: number;
	changeWorkPlaceDate: string;
	workStartDate: string;
	workEndDate: string;
	memo?: string | null;
	reason?: string;
	detailReason?: string;
}

export type TAssignmentCalendarWithDate = {
	id: number;
	name: string;
	requestNum: number;
	assignmentNum: number;
	day: string;
	startDate: string;
	endDate: string;
	startTime: string;
	endTime: string;
	typeDetail: string;
	applicantHistories: string | null;
	employerName: string;
	productionTypeDetail: string;
	publicFranchiseId: number;
	franchiseId: number;
	relaxTime: number;
	memo: string;
};

export type TGetApplicantAssignmentAutocompleteResponse = Array<{
	id: number;
	name: string;
}>;

export type TGetFranchiseAssignmentAutocompleteResponse = Array<{
	id: number;
	name: string;
}>;

type TStatistical = {
	assignmentApplicant: number;
	totalAssignmentApplicant: number;
	assignmentFranchise: number;
	totalAssignmentFranchise: number;
	lowRateApplicant: number;
	avgAssignmentHours: number;
};

type TStatisticalList = TStatistical & {
	publicFranchiseId: number;
	publicFranchiseName: string;
	assignmentApplicantDetails: string;
	assignmentFranchiseDetails: string;
	lowRateApplicantDetails: string;
};

type TWorkProgress = {
	completedAssignments: number;
	todayAssignments: number;
	remainingAssignments: number;
	totalAssignments: number;
};

type TWorkProgressList = TWorkProgress & {
	publicFranchiseName: string;
	completedFranchiseDetails: string;
	todayFranchiseDetails: string;
	remainingFranchiseDetails: string;
};

export type TGetPublicListWithDateResponse = {
	assignments: TAssignmentCalendarWithDate[];
	statistical: {
		statistical: TStatistical;
		statisticalList: TStatisticalList[];
	};
	workProgress: {
		workProgress: TWorkProgress;
		workProgressList: TWorkProgressList[];
	};
	totalPublicApplicantCnt: number;
};

export type TGetPublicAssignmentsResponse = TAssignmentCalendarWithDate[];

export type TGetPublicListWithDateParams = {
	publicMainFranchiseId?: string;
	searchType?: string;
	searchId?: string | null;
	startDate?: string;
	endDate?: string;
	programId?: string;
	downloadReason?: string;
	downloadFileName?: string;
};

export type TGetEnablePublicApplicantsParams = {
	date: string;
	publicMainFranchiseId?: string;
	page: number;
	pageSize: number;
	allData?: boolean;
};

export type TGetApplicantAssignmentAutocompleteParams = {
	publicFranchiseId?: string;
};

export type TGetFranchiseAssignmentAutocompleteParams = {
	publicFranchiseId?: string;
};

export type TEnablePublicApplicantItem = {
	applicantId: number;
	applicantName: string;
	entryDate: string;
	departureDate: string;
	gender: string;
	age: number;
	workingDayLast30Days: string;
};

export type TGetEnablePublicApplicantsResponse =
	TGetDataList<TEnablePublicApplicantItem>;

export type GetWorkerLodgingListParams = TGetDataSearchFilter & {
	allData?: boolean;
	ids?: number[];
};

export interface ICommonLodging {
	id: number;
	address: string;
	districtId: number;
	districtName: string;
	ownershipType: string;
	facilityType: string;
	providerName: string;
	providerType: string;
	providerPhone: string;
	providerTable: string;
	providerId: number;
	registDt: string;
}

export type WorkerLodgingItem = {
	id: number;
	address: string;
	districtId: number;
	ownershipType: string;
	facilityType: string;
	providedDate: string;
	inspectionDate: string;
	providerType: string;
	providerName: string;
	providerNationalCode: National;
	providerPhone: string;
	providerAddress: string;
	memo: string;
	providerId: number;
	providerTable: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	franchiseName: string;
	franchiseEmployeeName: string;
	employerBirthDate: string;
	franchiseBusinessRegistNo: string;
	franchiseCorporateRegistNo: string;
	firstApplicant: IWorkerLodgingApplicantDetailStore;
	lodgingCost: string;
	mealCost: string;
};

export type WorkerLodgingListItem = {
	lodgingAddress: string;
	lodgingAssignCnt: number;
	lodgingDistrictId: string;
	lodgingDistrictName: string;
	lodgingFacilityType: string;
	lodgingId: number;
	lodgingInspectionDate: string;
	lodgingMemo: string;
	lodgingOwnershipType: string;
	lodgingProvidedDate: string;
	lodgingProviderAddress: string;
	lodgingProviderId: string;
	lodgingProviderName: string;
	lodgingProviderNationalCode: string;
	lodgingProviderPhone: string;
	lodgingProviderTable: string;
	lodgingProviderType: string;
	lodgingRegistDt: string;
	lodgingUpdateDt: string;
	lodgingLodgingCost: string;
	lodgingMealCost: string;
};

export type GetWorkerLodgingListResponse = TGetDataList<WorkerLodgingItem>;

export type GetWorkerLodgingDetailResponse = WorkerLodgingItem;

export type IWorkerProviderLodgingItem = {
	id: number;
	name: string;
	birthDate: string;
	address: string;
	phone: string;
	registDt: string;

	lodgings: string;
	providerId: number;
	providerTable: string;

	recommenderId: number;
	recommenderName: string;

	providerNationalCode: National;
};

export interface IWorkerLodgingApplicantItem {
	id: number;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	lodgingRelationship: string;
	entryDate: string;
	departureDate: string;
	visaCode: string;
	statusId: number;
	status: string;
	memo: string;
}

export type GetWorkerLodgingApplicantListParams = TGetDataSearchFilter & {
	id: number;
	programId?: string | number;
};

export type TCreateWorkerLodgingApplicantBody = {
	applicantId: number;
	lodgingRelationship?: string;
};

export type GetWorkerLodgingApplicantListResponse =
	TGetDataList<IWorkerLodgingApplicantItem>;

export type TSettingUnit = {
	id: string;
	unitId: string;
	name: string;
	registId: number;
	registName: string;
	registDt: string;
};

export type TSettingUnitDistrict = TSettingUnit;

export type TSettingApplicantStatus = TSettingUnit & {
	isDefault: boolean;
	type: SettingUnitMasterInfoStatusEnum;
	statusType: boolean;
	issuanceStatusType: boolean;
};

export type TSettingOptionalField = TSettingUnit & {
	type: SettingUnitMasterInfoObjectEnum;
	fieldNo: number;
	dataType: SettingUnitMasterInfoFieldTypeEnum;
	applicantType: boolean;
	franchiseType: boolean;
};

export type TGetSettingMasterInfoResponse = {
	districtList: TSettingUnitDistrict[];
	statusList: TSettingApplicantStatus[];
	issuanceStatusList: TSettingApplicantStatus[];
	optionalFieldApplicantList: TSettingOptionalField[];
	optionalFieldFranchiseList: TSettingOptionalField[];
};

export type TUpdateSettingMasterInfoBody = {
	type: TypeUpdateSettingMasterInfoEnum;
	value: string;
	dataType?: SettingUnitMasterInfoFieldTypeEnum;
};

export type TDeleteSettingMasterInfoBody = {
	type: TypeUpdateSettingMasterInfoEnum;
	id: number;
};

export interface IWorkerSalary {
	applicantSalaryId: number;
	applicantSalaryApplicantFullName: string;
	applicantSalaryGrade: GradeEnum;
	applicantSalaryApplicantId: number;
	applicantSalaryEndTime: string;
	applicantSalaryMemo: string;
	applicantSalarySalary: number;
	applicantSalaryStartTime: string;
	applicantSalaryWorkingDate: string;
	applicantSalaryAddress: string;
	applicantSalaryEmployerName: string;
	applicantSalaryEmployerPhone: string;
	// franchiseGradeAHourlyWage: string;
	// franchiseGradeBHourlyWage: string;
	// franchiseGradeCHourlyWage: number;
	applicantSalaryFranchiseId: number;
	applicantSalaryPublicFranchiseId: number;
	applicantSalaryPublicFranchiseName: string;
	applicantSalaryHourlyWage: number;
	applicantSalaryRelaxTime: number;
}

export type GetWorkerSalaryListResponse = TGetDataList<IWorkerSalary>;

export interface GetWorkerSalaryLisParams extends TGetDataSearchFilter {
	franchiseId?: number | null;
	applicantName?: string | null;
	employerName?: string | null;
}

export interface ITimeInHour {
	hour: number;
	minute: number;
	second: number;
	nano: number;
}

export type TUpdateWorkerSalaryRequest = {
	ids: number[];
	startTime: string;
	endTime: string;
	salary: string;
	memo: string;
};

export type GetProviderInfoListParams = {
	name: string;
	type: string;
	districtId?: number;
} & GetListPageParams;

export type GetProviderInfoListResponse =
	TGetDataList<IWorkerProviderLodgingItem>;

export type CreateLodgingBody = {
	address: string;
	districtId: number;
	ownershipType: Ownership;
	facilityType: Infrastructure;
	providedDate: string;
	providerName: string;
	providerNationalCode: string;
	providerPhone: string;
	providerAddress: string;
	memo: string;
	providerId: number;
};

export type TResFile = {
	id: number; //id of file, used in db
	fileId: string; //id of file in minio
	fileSize: number;
	type: FileTypeEnum;
	path: string;
	name: string;
	documentType: DocumentTypeEnum;
	jsonContent: string;
};
export type TGetDefaultInfoUnitResponse = {
	id: number;
	name: string;
	cityProvince: string;
	countyDistrict: string;
	type: UnitTypeEnum;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	addressDetail: string;
	companySealFileId: string;
	subDomain: string;
	managerName: string;
	managerPhone: string;
	namePlateTitle: string;
	namePlateContent: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	email: string;
	resFile: TResFile;
	isActivatedApplicantNoti: boolean;
	isActivatedFranchiseNoti: boolean;
	isActivatedExpApplicantSms: boolean;
	isActivatedExpApplicantNoti: boolean;
};

export type TMappingDocumentItem = {
	id: number;
	fileId: number;
	refTableName: PageTypeEnum;
	refTableId: number;
	registDt: string;
	status?: StatusDocumentUpload;
};

export type TGetDocumentByPageParams = {
	refTableName: PageTypeEnum;
	refTableId: string; //id of object
	documentType?: DocumentTypeEnum[];
	page: number;
	pageSize: number;
	allData?: boolean;
	applicantId?: number;
	franchiseId?: number;
};

export type TGetCommonDocumentParams = {
	refTableName: PageTypeEnum;
	refTableId: number; //id of object
	documentType?: DocumentTypeEnum[];
	applicantId?: number;
	franchiseId?: number;
	programId?: number;
};

export type TDocumentDefineItem = {
	id: string;
	unitId: string;
	fileId: string;
	fileSize: number;
	type: FileTypeEnum;
	path: string;
	name: string;
	registId: number;
	registName: string;
	registDt: string | number;
	documentType: DocumentTypeEnum;
	jsonContent: string;
	fileMappingId: string;
	refTableName: PageTypeEnum;
	refTableId: number;
	mappingList: TMappingDocumentItem[];
	fileAuto: boolean;
	modified: boolean;
};

export type TIssueApplicationItem = {
	fileAuto?: boolean;
	registDtNumber?: number;
	id: string;
	unitId: string;
	fileId: string;
	fileSize: number;
	type: FileTypeEnum;
	path: string;
	name: string;
	registId: number;
	registName: string;
	registDt: string | number;
	documentType: DocumentTypeEnum;
	jsonContent: string;
	fileMappingId: string;
	refTableName: PageTypeEnum;
	refTableId: number;
	mappingList: TMappingDocumentItem[];
};

export type TDocumentUploadDefineItem = Omit<TDocumentDefineItem, 'id'> & {
	id: number;
};

export type TGetDocumentListByPageResponse = TGetDataList<TDocumentDefineItem>;

export type TGetDocumentDownloadParams = {
	refTableName: PageTypeEnum;
	refTableId: string;
	documentType: DocumentTypeEnum[];
};

export type TDocumentDefineDownloadItem = {
	documentType?: string;
	refTableName?: PageTypeEnum;
	fileList: TDocumentDefineItem[];
	refTableId?: string;
};

export type TDocumentDefineUploadItem = {
	documentType: DocumentTypeEnum;
	refTableName: PageTypeEnum;
	fileList: TDocumentUploadDefineItem[];
};

export type TGetDocumentDownloadListResponse = TDocumentDefineDownloadItem[];

export type TGetDocumentUploadListResponse = TDocumentDefineUploadItem[];

export type TDocumentFranchise = {
	employerName: string;
	franchiseId: number;
	fileMapingId: number;
};
export type TDocumentApplicant = {
	applicantFullName: string;
	applicantId: number;
	fileMapingId: number;
};
export type TDocumentRecommender = {
	recommenderName: string;
	recommenderId: number;
	fileMapingId: number;
};
export type TDocumentLodging = {
	lodgingName: string;
	lodgingId: number;
	fileMapingId: number;
	lodgingAddress: string;
};

export type IDocument = {
	fileId: number;
	fileUnitId: string;
	fileFileId: string;
	fileFileSize: number;
	fileType: FileTypeEnum;
	filePath: string;
	fileName: string;
	fileRegistId: number;
	fileRegistName: string;
	fileRegistDt: number | string;
	fileUpdateDt: number | string;
	fileDocumentType: DocumentTypeEnum;
	fileJsonContent: string;

	fileEmployerName: string | TDocumentFranchise[];
	fileApplicantFullName: string | TDocumentApplicant[];
	fileRecommenderName: string | TDocumentRecommender[];
	fileLodgingAddress: string | TDocumentLodging[];

	no?: number;
	type?: FileTypeEnum;
};
export type GetWorkerDocumentListResponse = TGetDataList<IDocument>;
export type GetWorkerDocumentListParams = TGetDataSearchFilter;

export type TDeleteWorkerDocumentRequest = Array<{
	fileId: number;
	fileMappingIds: number[];
}>;

export type TUpdateDocumentItemParams = {
	id: string;
	documentType?: DocumentTypeEnum;
	name?: string;
	'reqGroupRefer.id'?: number;
	'reqGroupRefer.refTableName'?: PageTypeEnum;
	'reqGroupRefer.refTableId'?: string;
	currentRefTableId?: string;
	currentRefTableName?: string;
	[key: string]: any;
};

export type TReqUpdateDocumentItem = {
	id: number;
	documentType: DocumentTypeEnum;
	reqReferList?: Array<{
		refTableName?: PageTypeEnum;
		refTableId?: number;
		id?: number;
	}>;
};

export type TUpdateDocumentBody = {
	refTableId: number;
	refTableName: PageTypeEnum;

	reqUpdateDocumentList?: TReqUpdateDocumentItem[];
};

export type TDeleteDocumentParams = {
	refTableName?: PageTypeEnum;
	refTableId?: string;
	id?: React.Key[];
	history: boolean;
};

export type TDeleteDocumentWithBody = {
	refTableName: PageTypeEnum;
	refTableId: string;
	id: string;
	history: boolean;
};

export type TDownloadDefineDocumentParams = {
	folder?: string | string[];
	path?: string;
	fileName?: string;
	downloadReason?: string;
	downloadFileName?: string;
};

export interface ISelectApplicantPopup {
	id: number;
	unitId: number;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	entryDate: string;
	visaCode: ResidenceEnum;
	visaTime: string;
	participationProgram: boolean;
	lodgingRelationship: string;
	recommenderRelationship: string;
}

export type ApplicantByLodgingId = {
	id: number;
	name: string;
	birthDay: string;
	recommenderRelationship: string;
	entryDate: string;
	status: string;
	memo: string;
};

export type GetApplicantListByLodgingIdResponse =
	TGetDataList<ApplicantByLodgingId>;

export interface GetWorkerUnauthorizedDepartureLisParams
	extends TGetDataSearchFilter {
	applicantSearchTypes?: TGetDataSearchFilter['searchTypes'];
	franchiseSearchTypes?: TGetDataSearchFilter['searchTypes'];
}

export type TDeleteWorkerUnauthorizedDepartureBody = Array<{
	id: number;
	applicantId: number;
	programId?: number;
}>;

export interface IApplicantItemUnauthorizedDeparute {
	accommodation: string;
	districtName: string;
	firstName: string;
	lastName: string;
	birthDate: string;
	gender: string;
	height: number;
	weight: number;
	phone: string;
	email: string;
	hometownAddress: string;
	hometownPhone: string;
	nationalCode: string;
	passportNo: string;
	passportIssueDate: string;
	passportExpiryDate: string;
	applicationType: string;
	isReentry: boolean;
	reentryCount: number;
	isPublic: boolean;
	publicFranchiseName: string;
	grade: string;
	statusId: string;
	issuanceStatusId: string;
	visaIssuanceConfirmNo: string;
	visaNo: string;
	foreignerNo: string;
	entryDate: string;
	departureDate: string;
	lodgingName: string;
	extensionStayDate: string;
	expirationDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	registDt: string;
	updateDt: string;
}

export interface IFranchiseItemUnauthorizedDeparute {
	// id: number;
	// unitId: number;
	email: string;
	// districtId: number;
	name: string;
	phone: string;
	type: string;
	typeDetail: string;
	productionType: number;
	productionTypeDetail: string;
	productionUnit: number;
	productionScale: string;
	address: string;
	employerName: string;
	employerBirthDate: string;
	employerPhone: string;
	employmentType: number;
	isPublic: number;
	// publicFranchiseId: number;
	businessRegistNo: string;
	corporateRegistNo: string;
	memo: number;
	// workStartTime: string;
	// workEndTime: string;
	// monthlyWorkingHours: number;
	// dailyBreaksNumber: number;
	// dailyBreaksHour: number;
	// dailyBreaksMin: number;
	requestApplicant: number;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	// isLack: number;
	hasLodging: number;
	// gradeADailyWage: number;
	// gradeBDailyWage: number;
	// gradeCDailyWage: number;
	// defaultGrade: string;
	// registId: number;
	// registName: string;
	registDt: string;
	updateDt: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	districtName: string;
	publicFranchiseName: number;
	requestRecentApplicant: number;
	totalApplicant: number;
}

export interface IWorkerUnauthorizedDepartureListItem
	extends IFranchiseListItem,
		IApplicantListItem {
	unauthorizedDepartureId: number;
	unauthorizedDepartureOccuringArea: string;
	unauthorizedDepartureOccuringDate: string;
	unauthorizedDepartureReason: string;
	unauthorizedDepartureRegistDt: number;
	unauthorizedDepartureUpdateDt: number;
	unauthorizedDepartureProgramId?: number;
}

export type GetWorkerUnauthorizedDepartureListResponse =
	TGetDataList<IWorkerUnauthorizedDepartureListItem>;

export type TCreateWorkerUnauthorizedDepartureRequest = {
	franchiseId: number;
	applicantId: number;
	programId: number;
	occuringArea: string | null;
	occuringAreaDetail: string;
	reason: string;
	occuringDate: string;
	memo: string;
};
export type GetSystemUnitListParams = TGetDataSearchFilter & {
	name?: string | null;
};

export interface IWorkerUnauthorizedDepartureDetail {
	franchiseId: number;
	franchiseName: string;
	franchiseBirthDate: string;
	franchisePhone: string;
	franchiseEmail: string;
	franchiseBusinessRegistNo: string;
	applicantId: number;
	applicantName: string;
	applicantBirthDate: string;
	applicantVisaCode: string;
	occuringArea: string;
	occuringAreaDetail: string;
	reason: string;
	occuringDate: string;
	memo: string;
	programId: number;
	workStartDate: string;
	workEndDate: string;
	registDt: string;
	updateDt: string;
	applicantForeignerNo: string;
	applicationVisaCodeName: string;
}

export type GetWorkerUnauthorizedDepartureDetailResponse =
	TGetDataList<IWorkerUnauthorizedDepartureDetail>;

export type IAssignmentHistoryLayer = {
	email: string;
	franchiseId: number;
	franchiseTypeDetail: string;
	franchiseName: string;
	workStartDate: string;
	workEndDate: string;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	requestApplicant: number;
	memo: string;
};

export type GetAssignmentHistoryLayerListResponse = IAssignmentHistoryLayer[];
export type GetAssignmentHistoryLayerParams = {
	franchiseId: number;
};

export type IPublicWorkHistoryLayer = {
	email: string;
	franchiseId: number;
	franchiseTypeDetail: string;
	franchiseName: string;
	workStartDate: string;
	workEndDate: string;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	requestApplicant: number;
	memo: string;
};

export type GetPublicWorkHistoryLayerListResponse = IPublicWorkHistoryLayer[];
export type GetPublicWorkHistoryLayerParams = {
	franchiseId: number;
};

export type TGetWorkHistoryLayerListParams = {
	applicantId: number;
};

export type TExtensionStayApplicantsItem = {
	applicantId: number;
	extensionPeriod: string;
	extensionDate: string;
	// programId: number;
};
export type TCreateWorkerExtensionStayRequest = {
	franchiseId: number;
	extensionStayApplicants: TExtensionStayApplicantsItem[];
	memo: string;
	programId: number;
};

export type GetApplicantByFranchiseExtensionStayParams =
	TGetDataSearchFilter & {
		franchiseId: number;
		programId: string;
		name: string;
		excludeExtensionStayId: string;
	};

export type IApplicantByFranchiseExtensionStay = {
	id: number;
	applicantId: number;
	firstName: string;
	lastName: string;
	birthDate: string;
	status: string;
	statusId: string;
	entryDate: string;
	visaCode: string;
	expirationDate?: string;
	extensionDate?: string;
	extensionPeriod: string;
	programId: number;
};
export type GetApplicantByFranchiseExtensionStayListResponse =
	Array<IApplicantByFranchiseExtensionStay>;

export type GetApplicantListByIdsParams = {
	applicantIds: number[];
};

export type IApplicantById = {
	id: number;
	applicantId: number;
	firstName: string;
	lastName: string;
	birthDate: string;
	status: string;
	statusId: string;
	entryDate: string;
	visaCode: string;
	stayExpirationDate?: string;
	expirationDate?: string;
	extensionDate?: string;
	extensionStayDate?: string;
	extensionPeriod: string;
	programId: number;
};
export type GetApplicantListByIdsResponse = Array<IApplicantById>;

export interface IWorkerExtensionStay {
	id: number;
	franchiseId: number;
	employerName: string;
	employerBirthDate: string;
	employerPhone: string;
	franchiseName: string;
	address: string;
	productionType: string;
	productionTypeDetail: string;
	memo: number;
	registDt: string;
	updateDt: string;
	programId: number;
	extensionStayApplicantId: number;
}
export type GetWorkerExtensionStayListResponse =
	TGetDataList<IWorkerExtensionStay>;

type TCommonYearRegisterList = keyof typeof CommonYearRegisterListEnum;
type TPageYearList = keyof typeof PageYearListEnum;
export type TYearScreenCode = TCommonYearRegisterList | TPageYearList;

export interface IAssignmentItem {
	id: number;
	additionalAssignmentApplicant: string;
	assignmentApplicant: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	districtId: string;
	employerAddress: string;
	employerBirthDate: string;
	employerName: string;
	employerPhone: string;
	employmentType: string;
	franchiseEmail: string;
	franchiseName: string;
	franchisePhone: string;
	isPublic: string;
	productType: string;
	productionScale: string;
	productionTypeDetail: string;
	productionUnit: string;
	publicFranchiseId: string;
	registDt: string;
	requestApplicant: string;
	type: string;
	typeDetail: string;
	updateDt: string;
	workEndDate: string;
	workStartDate: string;
	address: string;
	applicantId: number;
	birthDate: string;
	email: string;
	fullName: string;
	phone: string;
	visaCode: string;
	workStartEndDate: string;
	applicantBirthDate: string;
	franchiseEmployerBirthDate: string;
}
export type TUpdateFranchiseWorkerExtensionStayRequest = {
	id: number;
	franchiseId: number;
};

export interface IApplicantForExtensionStay {
	id: number;
	applicantId: number;
	firstName: string;
	lastName: string;
	birthDate: string;
	status: string;
	statusId: number;
	entryDate: string;
	visaCode: number;
	expirationDate: string;
	extensionDate: string;
	extensionPeriod: string;
	programId: number;
}
export type GetApplicantForExtensionStayListResponse =
	TGetDataList<IApplicantForExtensionStay>;

export type TDeleteApplicantExtensionStayRequest = {
	applicantId: number;
	extensionStayApplicantId: number;
};

export type TCreateApplicantForExtensionStayRequest = {
	franchiseId: number;
	extensionStayApplicants: TExtensionStayApplicantsItem[];
};

export type WFranchiseItem = {
	[K in keyof IFranchiseItem as `franchise${Capitalize<K & string>}`]: IFranchiseItem[K];
} & { newId: number; newUnitId: number };

export interface IExtensionStay extends IFranchiseListItem, IApplicantListItem {
	id: number;
	extensionStayId: number;
	extensionStayApplicantExpirationDate: string;
	extensionStayApplicantExtensionDate: string;
	extensionStayApplicantExtensionPeriod: string;
	extensionStayApplicantRegistDt: number;
	extensionStayApplicantUpdateDt: number;
	extensionStayApplicantId: number;
	extensionStayApplicantExtensionStayId: number;
	applicant: IApplicantItemUnauthorizedDeparute;
	franchise: IFranchiseItemUnauthorizedDeparute;
}

export type GetExtensionStayListResponse = TGetDataList<IExtensionStay>;

export type GetExtensionStayListParams = TGetDataSearchFilter & {
	districtId?: number | null;
	applicantName?: string | null;
	employeeName?: string | null;
	allData?: boolean;
};

export interface GetDashboardListParams
	extends Omit<TGetDataSearchFilter, 'searchTypes' | 'exportExcel'> {
	programId?: number | null;
	status?: EDashboardStatisticEnum | null;
	fullName?: string | null;
	isResidencyExpiryD60?: boolean;
}

export interface GetDashboardExcelListBody {
	startDate: string;
	endDate: string;
	programIds: number[];
	downloadReason: string;
	downloadFileName: string;
}

export interface IDashboardStatistic {
	countNormal: number;
	countPublic: number;
	countAll: number;
	statisticalType: EDashboardStatisticEnum;
}
export interface IForeignDashboardStatistic {
	year: number;
	applicantCount: number;
}

export type GetDashboardStatisticResponse = IDashboardStatistic[];
export type GetForeignDashboardStatisticResponse = IForeignDashboardStatistic[];
export type GetDashboardStatisticParams = {
	programId?: number;
};

export interface IDashboardNotice {
	id: number;
	title: string;
	content: string;
	targetUnitId: number;
	startDt: string;
	endDt: string;
	memo: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	files: TGetDocumentDetailResponse[];
	targetUnitName: string;
	targetNotice: string;
}

export type GetDashboardNoticeListResponse = IDashboardNotice[];

export interface IDashboardInquiry {
	id: number;
	unitId: number;
	type: string;
	title: string;
	contents: string;
	answer: string;
	answerUserId: number;
	answerUserName: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	unitName: string;
	status: string;
}

export type GetDashboardInquiryListResponse = IDashboardInquiry[];

export type GetCommonDocumentObjectListParams = {
	searchType?: DocumentObjectListSearchTypeEnum;
	searchValue?: string;
	refTableName: DocumentObjectListRefTableEnum;
	searchTypes?: GetListSearchTypes['searchTypes'];
};

export type GetCommonDocumentObjectListResponse =
	| IApplicantListItem[]
	| IFranchiseListItem[]
	| WorkerLodgingListItem[]
	| IRecommenderListItem[]
	| IWorkerProgram[];

export type IAutocompleteSuggestionItem = {
	id: number;
	value: string;
	providerName?: string;
	fullName?: string;
};

export type TGetAutocompleteSuggestionResponse = {
	applicants: IAutocompleteSuggestionItem[];
	franchises: IAutocompleteSuggestionItem[];
	lodgings: IAutocompleteSuggestionItem[];
	recommenders: IAutocompleteSuggestionItem[];
	changeWorkPlaces: IAutocompleteSuggestionItem[];
	unauthorizedDepartures: IAutocompleteSuggestionItem[];
	assignmentFranchises: IAutocompleteSuggestionItem[];
};

export type TGetAutocompleteManagerNameResponse = string[];

export type ICommonApplicant = {
	id: number;
	workStartDate: string;
	workEndDate: string;
	extensionStayDate: string;
	expirationDate: string;
	changeWorkDate: string;
	unauthorizedDepartureDate: string;
	unitId: number;
	districtId: number;
	email: string;
	firstName: string;
	lastName: string;
	fullName: string;
	birthDate: string;
	gender: string;
	height: number;
	weight: number;
	phone: string;
	hometownAddr: string;
	hometownPhone: string;
	nationalCode: string;
	passportNo: string;
	passportIssueDate: string;
	passportExpireDate: string;
	applicationType: string;
	reEntryCount: number;
	publicFranchiseId: number;
	grade: string;
	statusId: number;
	issuanceStatusId: number;
	visaIssuanceConfirmNo: string;
	visaNo: string;
	foreignerNo: string;
	entryDate: string;
	departureDate: string;
	visaCode: string;
	customField1: string;
	customField2: string;
	customField3: string;
	customField4: string;
	customField5: string;
	memo: string;
	registId: number;
	registName: string;
	assignmentApplicantId: number;
	applicantMemo: string;
	age: number;
	recommenderName: string;
	providerName: string;
	canAssign: boolean;
	hasWorkInFranchise: boolean;
	workingHistory: string;
	registDt: string;
	freeDateCount: number;
	lastCheckDate: string;
	nextCheckDate: string;
	reEntry: boolean;
	public: boolean;
	dailyWage: number;

	disabled?: boolean;
	hasError?: boolean;
};

export type GetCommonApplicantListResponse = TGetDataList<ICommonApplicant>;

export interface IWCFranchiseAssignmentItem {
	id: number;
	programId: number;
	franchiseId: number;
	type: string;
	requestApplicant: string;
	assignmentApplicant: string;
	additionalAssignmentApplicant: string;
	startDate: string;
	endDate: string;
	memo: string;
	registId: string;
	registName: string;
	registDt: string;
	updateDt: string;
	startEndDate: string;
}

export interface ICommonAssignmentApplicant {
	assignmentApplicantId: number;
	applicantId: number;
	applicantFirstName: string;
	applicantLastName: string;
	applicantFullName: string;
	applicantBirthDate: string;
	applicantDistrictId: number;
	franchiseName: string;
	franchiseAddress: string;
	workStartDate: string;
	workEndDate: string;
	registDt: string;
	workStartEndDate?: string;
	assignmentFranchiseId?: number;
	applicantVisaCode: string;
	statusName: string;
	franchisePhone: string;
	franchiseEmail: string;
	franchiseBusinessRegistNo: string;
	franchiseId: number;
	franchiseEmployerName: string;
	franchiseEmployerPhone: string;
	assignmentFranchiseProgramId: number;
	franchiseEmployerBirthDate: string;
	franchiseProductionType: string;
	franchiseProductionTypeDetail: string;
	programName: string;
	programStartDate: string;
	programEndDate: string;
	assignmentFranchiseStartDate: string;
	assignmentFranchiseEndDate: string;
	franchiseDistrictId: number;
	franchiseDistrictName: string;
	applicantDistrictName: string;
}

export interface IWCFranchiseItem {
	id: number;
	address: string;
	employerName: string;
	phone: string;
	email: string;
	businessRegistNo: string;
	districtId: number;
	districtName: string;
}

export type TApplicantDashboardExcelResponse = ICommonApplicant & {
	typeDetail: string;
	employerName: string;
	employerBirthDate: string;
	franchiseName: string;
	corporateRegistNo: string;
	address: string;
	businessRegistNo: string;
	employerPhone: string;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	applicantFullName: string;
	applicantBirthDate: string;
	assignmentApplicantCnt: number;
	additionalAssignmentApplicantCnt: number;
};

export type TEmployerDashboardExcelResponse = IFranchiseItem & {
	assignmentApplicantCnt: number;
	additionalAssignmentApplicantCnt: number;
};

export type TCurrentSituationExcelListResponse = {
	franchises: TEmployerDashboardExcelResponse[];
	applicants: TApplicantDashboardExcelResponse[];
	unit: TSystemUnitItem;
};

export type IItemWorkerProgramAllResponse = {
	id: number;
	name: string;
	startDate: string;
	endDate: string;
};

export type GetWorkerProgramListAllResponse = IItemWorkerProgramAllResponse[];

export type TGetApplicantSalaryAutocompleteApplicantNameResponse = Array<{
	id: number;
	name: string;
}>;

export type TUpdateWorkerDocumentItem = {
	documentType?: DocumentTypeEnum;
	name?: string;
	reqGroupRefer?: {
		id?: number;
		refTableName?: PageTypeEnum;
		refTableId?: string;
	};
	[key: string]: any;
	jsonContent?: string;
};

export interface TGetDocumentDetailResponse {
	id: number;
	unitId: number;
	fileId: string;
	fileSize: number;
	type: string;
	path: string;
	name: string;
	registId: number;
	registName: string;
	registDt: number;
	documentType: string;
	jsonContent: string;
	fileMappingId: number | null;
	refTableName: string | null;
	refTableId: number | null;
	mappingList: TMappingDocumentItem[];
	save: boolean;
}

export type IDashboardStatisticFranchise = {
	franchiseByTypeCount: {
		countTypePublic: number;
		countTypeNormal: number;
	};
	franchiseByDistrictCount: TFranchiseDistrict[];
	franchiseByTypeDetailCount: {
		countTypeAgriculture: number;
		countTypeFishing: number;
		countTypePublic: number;
	};
	franchiseAssignmentCount: {
		countFranchiseAssignment: number;
		countFranchiseNotAssignment: number;
	};
};

export type TFranchiseDistrict = {
	districtId: number | null;
	districtName: string;
	countNumberOfFranchise: number;
};

export type TApplicantNational = {
	nationalCode: string;
	countNumberOfApplicant: number;
};

export type TApplicantDistrict = {
	districtId: number;
	districtName: string;
	countNumberOfApplicant: number;
};

export interface IDashboardStatisticApplicant {
	applicantByTypeCount: {
		allApplicantCount: number;
		normalApplicantCount: number;
		mouApplicantCount: number;
		relativesApplicantCount: number;
		publicApplicantCount: number;
		domesticStayApplicantCount?: number;
		otherApplicantCount?: number;
		studentParentInvitationApplicantCount?: number;
	};
	applicantByDistrictCount: TApplicantDistrict[];
	applicantJoinProgramCount: {
		hasJoinProgramCount: number;
		noJoinProgramCount: number;
	};
	applicantAssignmentCount: {
		assignedCount: number;
		notAssignedCount: number;
	};
	applicantGenderCount: {
		maleCount: number;
		femaleCount: number;
	};
	applicantNationalCounts: TApplicantNational[];
}

export type GetDashboardStatisticFranchiseResponse =
	IDashboardStatisticFranchise;
export type GetDashboardStatisticApplicantResponse =
	IDashboardStatisticApplicant;

export type GetCommonPopupLodgingListResponse = TGetDataList<any>;

export type UploadExcelUserInfo = {
	districtId: number;
	districtName: string;
	role: RoleEnum;
};

export type GetLatestAssignmentResponse = {
	id: number;
	programId: number | null;
	franchiseId: number;
	type: string | null;
	publicFranchiseId: number | null;
	requestApplicant: number;
	assignmentApplicant: number;
	additionalAssignmentApplicant: number;
	startDate: string;
	endDate: string;
	memo: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string | null;
};

export type GetDistrictValidatorResponse = {
	SYS_ID_RECORD_HAS_ASSIGN: number[];
	AM1_INVALID_DISTRICT_ID: number[];
};

export type ILastedAssignment = {
	franchiseEmployerName: string;
	franchiseName: string;
	franchisePhone: string;
	id: number;
	programId: number;
	workEndDate: string;
	workStartDate: string;
};

export type GetLastedAssignmentResponse = ILastedAssignment[];

export type GetMSelectLodgingPopupListResponse = TGetDataList<any>;

export interface IContractedKoreanBusiness {
	id: number;
	no: number;
	name: string;
	cityProvince: string;
	countyDistrict: string;
	type: UnitTypeEnum;
	typeName: string;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	addressDetail: string;
	companySealFileId: number;
	subDomain: string;
	managerName: string;
	managerPhone: string;
	namePlateTitle: string;
	namePlateContent: string;
	isActivatedApplicantNoti: true;
	isActivatedFranchiseNoti: true;
	isActivatedExpApplicantNoti: true;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	email: string;
	unitRegionType: string;
	userId: number;
}
export type GetContractedKoreanBusinessResponse =
	TGetDataList<IContractedKoreanBusiness>;

export type TGetForeignDefaultInfoUnitResponse = {
	registDt: string;
	updateDt: string;
	id: number;
	name: string;
	type: UnitTypeEnum;
	phone: string;
	address: string;
	foreignAddress: string;
	subDomain: string;
	managerName: string;
	managerPhone: string;
	registId: number;
	registName: string;
};

export type TGetForeignDomesticUnitListResponse = {
	content: {
		id: number;
		name: string;
	}[];
};
export type ForeignProgram = {
	programUnitName: string;
	programForeignUnitType: string;
	programForeignStartDate: string;
	programForeignEndDate: string;
	programForeignUpdateDt: number;
	programForeignId: number;
	programForeignName: string;
	programForeignRegistDt: number;
};

export type GetForeignProgramListResponse = TGetDataList<ForeignProgram>;

export type GetForeignProgramListParams = TGetDataSearchFilter & {
	name?: string | null;
	programStartDate?: string | null;
	programEndDate?: string | null;
	registStartDate?: string | null;
	registEndDate?: string | null;
	allData?: boolean;
};

export type IForeignProgram = {
	id: number;
	unitId: number;
	unitName: string;
	unitType: string;
	name: string;
	startDate: string;
	endDate: string;
	memo: string | null;
	farmCnt: number;
	agriculturalCoCnt: number;
	publicFranchiseCnt: number;
	fishingCnt: number;
	corporateFishingCnt: number;
	franchiseCnt: number;
	mouApplicantCnt: number;
	marriageImmigrantsCnt: number;
	foreignersStayingCnt: number;
	reEntryCnt: number;
	applicantCnt: number;
	extensionStayCnt: number;
	workplaceChangeCnt: number;
	unauthorizedDepartureCnt: number;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string | null;
	registType: string | null;
	status: string | null;
};

export type GetForeignProgramDetailResponse = IForeignProgram;

export type GetForeignProgramApplicantListParams = TGetDataSearchFilter & {
	name?: string | null;
};

export type GetForeignProgramApplicantListResponse = Omit<
	TGetDataList<IProgramApplicant>,
	'searchTypes'
>;

export type TGetFooterInfoListParams = {
	isSystemAdmin: boolean;
	unitId?: number;
};

export type TFooterInfo = {
	id: number;
	unitId: number;
	title: string;
	url: string;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
};

export type TGetFooterInfoListResponse = TFooterInfo[];

export type TCreateFooterInfoRequestParams = {
	title: string;
	url: string;
};

export type TGetListContractedForeignBusinessParams = GetListPageParams & {
	allData?: boolean;
	id: number;
};

export interface IContractedForeignBusiness {
	id: number;
	no: number;
	name: string;
	cityProvince: string;
	countyDistrict: string;
	type: UnitTypeEnum;
	typeName: string;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	addressDetail: string;
	companySealFileId: number;
	subDomain: string;
	managerName: string;
	managerPhone: string;
	namePlateTitle: string;
	namePlateContent: string;
	isActivatedApplicantNoti: true;
	isActivatedFranchiseNoti: true;
	isActivatedExpApplicantNoti: true;
	isActivatedApplicantSms: true;
	isActivatedFranchiseSms: true;
	isActivatedExpApplicantSms: true;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	email: string;
	unitRegionType: string;
	userId: number;
	nationalCode: string;
	isActive: boolean;
}

export type GetContractedForeignBusinessResponse =
	TGetDataList<IContractedForeignBusiness>;
export type GetApplicantFormInfoResponse = Array<{
	id: number;
	fullName: string;
	foreignerNo: string | null;
	nationalCode: string;
	passportNo: string | null;
	expirationDate: string | null;
	extensionPeriod: ExtensionStayEnum | null;
	departureDate: string | null;
	visaCode: ResidenceEnum | null;
}>;

export type TForeignSystemUnitItem = {
	id: number;
	name: string;
	type: string;
	phone: string;
	businessRegistNo: string;
	corporateRegistNo: string;
	zipcode: string;
	address: string;
	foreignAddress: string;
	subDomain: string;
	email: string;
	managerName: string;
	managerPhone: string;
	namePlateTitle: string;
	namePlateContent: string;
	isActivatedApplicantNoti: boolean;
	isActivatedFranchiseNoti: boolean;
	isActivatedExpApplicantNoti: boolean;
	isActivatedExpApplicantSms: boolean;
	registId: number;
	registName: string;
	registDt: string;
	updateDt: string;
	unitRegionType: string;
	userId: number;
	nationalCode: string;
	isActive: boolean;
};

export type TGetForeignSystemUnitListResponse =
	TGetDataList<TForeignSystemUnitItem>;

export type TForeignSystemHistoryUpdateUnitItem = {
	id: number;
	fieldName: string;
	title: string;
	prevData: string;
	afterData: string;
	registName: string;
	registDt: string;
};

export type TGetForeignSystemHistoryUpdateUnitListResponse =
	TGetDataList<TForeignSystemHistoryUpdateUnitItem>;

export type TGetListForeignSystemUnitParams = TGetDataSearchFilter & {
	name?: string | null;
};

export type TCreateForeignSystemUnitRequest = {
	nationalCode: string;
	type: string;
	name: string;
	phone: string;
	managerName: string;
	managerPhone: string;
	foreignAddress: string;
	subDomain: string;
	email: string;
	password: string;
};

export type TGetListContractedKoreanBusinessParams = GetListPageParams & {
	allData?: boolean;
	id?: number;
};

export type TCommonGetAppUserInfoBody = {
	id: number;
	type: PageTypeEnum;
};

export type TAppUserInfoItem = {
	id: number;
	email: string;
	registDt: string;
	lastestLoggedInDt: string;
	activatedNoti: boolean;
	activatedExpApplicantNoti: boolean;
	refTableName: PageTypeEnum;
	refTableId: number;
	withdrawnDate: string;
	notiRegistDt: string;
	expApplicantNotiRegistDt: string;
	agreementDt: string;
};
export type GetAppUserInfoResponse = TAppUserInfoItem;
export type TGetRelationDocumentInfoParams = {
	id: number;
	tableEnum: PageTypeEnum;
};

export type TRelationDocumentInfo = {
	applicant?: {
		franchise?: {
			id: number;
			assignmentRegistDt: number;
		};
		lodging?: number;
		recommender?: number;
		applicant?: {
			id: number;
			applicationType: ApplicantTypeEnum;
		};
	} | null;
	franchise?: {
		lodging?: Array<{
			id: number;
		}>;
		lodgingApplicant?: [
			{
				applicant?: number;
				lodging?: number;
			},
		];
	} | null;
	lodging?: {
		applicant?: number[];
		franchise?: number;
		recommender?: number;
	} | null;
	recommender?: {
		applicant?: {
			id: number;
			applicationType: ApplicantTypeEnum;
		}[];
		lodging?: Array<{
			id: number;
		}>;
	} | null;
};

export interface GetWorkerUseTipListParams extends TGetDataSearchFilter {}
export interface GetSystemUseTipListParams extends TGetDataSearchFilter {}

export type IWorkerUseTip = {
	id: number;
	title: string;
	content: string;
	contentOnlyText: string;
	registDt: string;
	attachmentList: TGetDocumentDetailResponse[];
	registName: string;
	updateDt: string;
	targetNotice: string;
};

export interface ISystemUseTip extends IWorkerUseTip {}

export type GetWorkerUseTipListResponse = TGetDataList<IWorkerUseTip>;
export type GetSystemUseTipListResponse = TGetDataList<ISystemUseTip>;

export type GetDashboardUseTipListResponse = IWorkerUseTip[];
export type GetDashboardNoticeReceiveListResponse = IWorkerUseTip[];

export type TCreateSystemUseTipRequest = {
	title: string;
	content: string;
	attachFileIds?: number[];
	contentFileIds?: number[];
};

export interface IWorkerUpdateHistoryTarget {
	refTableName: string;
	refTableId: number;
	refTableValue: string;
}

export interface IWorkerUpdateHistory {
	id: number;
	menu: AreaRefTableNameEnum;
	afterTarget: IWorkerUpdateHistoryTarget;
	beforeTarget: IWorkerUpdateHistoryTarget;
	updateArea: EUpdateAreaEnum;
	fieldName: string;
	prevData: string;
	afterData: string;
	registName: string;
	registDt: number;
}

export type GetWorkerHistoryListResponse = TGetDataList<IWorkerUpdateHistory>;

export interface GetWorkerHistoryListParams extends GetListPageParams {
	afterRefTableName?: AreaRefTableNameEnum;
	afterRefTableValue?: string;
	prevRefTableValue?: string;
}

export type GetDocumentMappingsBody = {
	refTableName: PageTypeEnum;
	refTableId: number[];
	isDefineUpload?: boolean;
};

export type GetDocumentMappingsResponse = Array<
	TMappingDocumentItem & {
		refName: string;
	}
>;

export interface IPublicSalaryListResponse {
	franchisePublicId: number;
	franchisePublicName: string;
	applicantCnt: number;
	dayWorkAvg: number;
	monthWorkAvg: number;
	salary: number;
	lodgingCost: number;
	totalCost: number;
}

export interface IPublicSalaryTotalResponse {
	franchisePublicCnt: number;
	applicantCnt: number;
	dayWorkAvg: number;
	monthWorkAvg: number;
	salary: number;
	lodgingCost: number;
	totalCost: number;
}

export interface IPublicSalaryDetailResponse {
	franchisePublicId: number;
	franchisePublicName: string;
	applicantCnt: number;
	dayWorkAvg: number;
	monthWorkAvg: number;
	salary: number;
	lodgingCost: number;
	totalCost: number;
}

export interface IPublicSalaryExcelListResponse {
	franchisePublicId: number;
	franchisePublicName: string;
	applicantCnt: number;
	dayWorkAvg: number;
	monthWorkAvg: number;
	salary: number;
	lodgingCost: number;
	totalCost: number;
}

export type GetPublicSalaryListResponse = {
	total: IPublicSalaryTotalResponse;
	list: TGetDataList<IPublicSalaryListResponse>;
};
export type GetPublicSalaryDetailResponse = GetPublicSalaryListResponse;
export type GetPublicSalaryExcelListResponse = {
	total: IPublicSalaryTotalResponse;
	list: TGetDataList<IPublicSalaryListResponse>;
};

export interface IPublicSalaryApplicantListItem {
	applicantId: number;
	applicantName: string;
	applicantBirthDate: string;
	applicantEntryDate: string;
	applicantDepartureDate: string;
	workStartDate: string;
	workEndDate: string;
	dayWorkCnt: number;
	monthWorkCnt: number;
	grade: string;
	salaryDay: number;
	totalSalary: number;
	lodgingCost: number;
	totalLodgingCost: number;
	mealCost: number;
	totalMealCost: number;
	totalLodgingMealCost: number;
	totalCost: number;
}

export type GetPublicSalaryApplicantListResponse =
	TGetDataList<IPublicSalaryApplicantListItem>;
export type GetPublicSalaryApplicantListParams = TGetDataSearchFilter & {};

export interface IWorkerAuditMonitoringListItem {
	action: string;
	auditUnitId: number;
	content: string;
	fileName: string | null;
	ip: string;
	jsonData: string | null;
	registDt: number;
	type: string;
	userEmail: string;
	userId: number;
	userName: string;
}

export type GetWorkerAuditMonitoringListResponse =
	TGetDataList<IWorkerAuditMonitoringListItem>;
export type GetWorkerAuditMonitoringListParams = TGetDataSearchFilter & {};

/* ---- DISTRICT ALLOW IP ---- */
export interface IDistrictAllowIpItem {
	id: number;
	unitId: number;
	districtId: number;
	districtName: string;
	ipAddress: string;
	registId: number;
	registName: string;
	registDt: number;
	updateDt: number | null;
}

export type GetDistrictAllowIpListResponse = IDistrictAllowIpItem[];

export type TCreateDistrictAllowIpRequest = {
	districtId?: number | null;
	ipAddress?: string | null;
};

export type TDeleteDistrictAllowIpRequest = number[];

/* ---- DEVICE MOBILE TYPES START---- */
export type TDeviceMobileStatus =
	| 'PENDING'
	| 'APPROVED'
	| 'REJECTED'
	| 'REVOKED';

export interface IDeviceMobileItem {
	id: number;
	adminId: number;
	adminName: string;
	account: string;
	osInfo: string;
	browserInfo: string;
	status: TDeviceMobileStatus;
	requestedAt: string;
}

export type TGetDeviceMobileListResponse = TGetDataList<IDeviceMobileItem>;

export interface IGetDeviceMobileListParams extends TGetDataSearchFilter {
	adminId?: number;
	status?: TDeviceMobileStatus | '';
	startDate?: string;
	endDate?: string;
}
/* ---- DEVICE MOBILE TYPES END---- */

/* ---- DEVICE TABLET TYPES START---- */
export type TDeviceTabletStatus = 'ACTIVE' | 'INACTIVE' | 'EXPIRED';

export interface IDeviceTabletItem {
	id: number;
	name: string;
	registeredAt: string;
	status: TDeviceTabletStatus;
	validFrom: string;
	validUntil: string;
	activatedByName: string;
	purpose: string;
	registDt: number;
}

export type TGetDeviceTabletListResponse = TGetDataList<IDeviceTabletItem>;

export interface IGetDeviceTabletListParams extends TGetDataSearchFilter {
	tabletName?: string;
	status?: TDeviceTabletStatus | '';
}
/* ---- DEVICE TABLET TYPES END---- */

/* ---- DEVICE LOG TYPES START---- */
export type TDeviceLogDeviceType = 'MOBILE' | 'TABLET';
export type TDeviceLogResult = 'SUCCESS' | 'BLOCKED';

export interface IDeviceLogItem {
	logId: number;
	adminName: string | null;
	deviceType: TDeviceLogDeviceType;
	deviceName: string;
	ipAddress: string;
	result: TDeviceLogResult;
	blockReason: string | null;
	actionType: string | null;
	registDt: string;
}

export type TGetDeviceLogListResponse = TGetDataList<IDeviceLogItem>;

export interface IGetDeviceLogListParams extends TGetDataSearchFilter {
	adminId?: number;
	deviceType?: TDeviceLogDeviceType | '';
	result?: TDeviceLogResult | '';
	startDate?: string;
	endDate?: string;
}
/* ---- DEVICE LOG TYPES END---- */

/* ---- DEVICE MOBILE ADMIN API TYPES START---- */
// Response: GET /api/v1/worker/device/mobile/{id}
export interface IMobileDeviceRecentLog {
	id: number;
	registDt: string;
	ipAddress: string;
	actionType: string;
	blockReason: string;
	result: string;
}

export interface IDeviceItem {
	id: number;
	adminId: number;
	adminName: string;
	account: string;
	osInfo: string;
	browserInfo: string;
	fingerprintHash: string;
	status: TDeviceMobileStatus;
	rejectReason: string | null;
	registDt: string;
	approvedAt: string | null;
	approvedByName: string | null;
	tokenExpiresAt: string | null;
}
export interface IDeviceMobileDetailResponse {
	device: IDeviceItem;
	recentLogs: IMobileDeviceRecentLog[];
}

// Response: POST /api/v1/device/mobile/{deviceId}/approve
export interface IDeviceMobileApproveResponse {
	deviceId: number;
	status: TDeviceMobileStatus;
	approvedAt: string;
	tokenExpiresAt: string;
}

// Request: POST /api/v1/device/mobile/{deviceId}/reject
export interface IDeviceMobileRejectRequest {
	rejectReason: string;
}

// Response: POST /api/v1/device/mobile/{deviceId}/reject
export interface IDeviceMobileRejectResponse {
	deviceId: number;
	status: TDeviceMobileStatus;
	rejectReason: string;
}

// Response: POST /api/v1/device/mobile/{deviceId}/revoke
export interface IDeviceMobileRevokeResponse {
	deviceId: number;
	status: TDeviceMobileStatus;
}

// Response: POST /api/v1/device/mobile/{deviceId}/reapprove
export interface IDeviceMobileReapproveResponse {
	deviceId: number;
	status: TDeviceMobileStatus;
	approvedAt: string;
	tokenExpiresAt: string;
}
/* ---- DEVICE MOBILE ADMIN API TYPES END---- */

/* ---- DEVICE TABLET ADMIN API TYPES START---- */
// Request: POST /api/v1/device/tablet/register-code
export interface IDeviceTabletRegisterCodeRequest {
	tabletName: string;
}

// Response: POST /api/v1/device/tablet/register-code
export interface IDeviceTabletRegisterCodeResponse {
	id: number;
	codeValue: string;
	tabletName: string;
	expiresAt: string;
}

// Request: POST /api/v1/device/tablet/{tabletId}/activate
export interface IDeviceTabletActivateRequest {
	validFrom: string;
	validUntil: string;
	purpose?: string;
}

// Response: POST /api/v1/device/tablet/{tabletId}/activate
export interface IDeviceTabletActivateResponse {
	tabletId: number;
	activationId: number;
	status: TDeviceTabletStatus;
	validFrom: string;
	validUntil: string;
}

// Response: POST /api/v1/device/tablet/{tabletId}/deactivate
export interface IDeviceTabletDeactivateResponse {
	tabletId: number;
	status: TDeviceTabletStatus;
}

// Response: POST /api/v1/device/tablet/{tabletId}/remote-code
export interface IDeviceTabletRemoteCodeResponse {
	id: number;
	codeValue: string;
	tabletName: string;
	expiresAt: string;
}

// Response: GET /api/v1/worker/device/tablet/{id}
export type TTabletActivationStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';

export interface ITabletCurrentActivation {
	activationId: number;
	validFrom: string;
	validUntil: string;
	activatedByName: string;
	purpose: string;
}

export interface ITabletActivationHistoryItem {
	id: number;
	validFrom: string;
	validUntil: string;
	activatedByName: string;
	purpose: string;
	status: TTabletActivationStatus;
}

export interface ITabletRecentLog {
	logId: number;
	registDt: string;
	adminName: string;
	ipAddress: string;
}

export interface ITabletItem {
	id: number;
	name: string;
	status: TDeviceTabletStatus;
	registDt: string;
	registeredByName: string;
}

export interface IRecentLog {
	id: number;
	registDt: number;
	adminName: string;
	ipAddress: string;
}
export interface IDeviceTabletDetailResponse {
	tablet: ITabletItem;
	currentActivation: ITabletCurrentActivation | null;
	activationHistory: ITabletActivationHistoryItem[];
	recentLogs: IRecentLog[];
}

export interface IGetTabletActivationsParams {
	page?: number;
	pageSize?: number;
}

export type TGetTabletActivationsResponse =
	TGetDataList<ITabletActivationHistoryItem>;
/* ---- DEVICE TABLET ADMIN API TYPES END---- */

/* ---- DEVICE CLIENT TYPES START---- */
export type TDeviceType = 'MOBILE' | 'TABLET';

export type TDeviceStatus =
	| 'UNREGISTERED'
	| 'PENDING'
	| 'REJECTED'
	| 'APPROVED'
	| 'REVOKED'
	| 'INACTIVE'
	| 'ACTIVE'
	| 'EXPIRED';

export type TDeviceScreen =
	| 'REGISTER'
	| 'PENDING_NOTICE'
	| 'REJECTED_NOTICE'
	| 'LOGIN'
	| 'INACTIVE_NOTICE';

export type TTypeAccess = 'OFFICE' | 'EXTERNAL';

// Request: POST /api/v1/device/client/check
export interface IDeviceCheckRequest {
	fingerprint?: string;
	deviceType: TDeviceType;
}

// Response: POST /api/v1/device/client/check
export interface IDeviceCheckResponse {
	deviceStatus: TDeviceStatus;
	screen: TDeviceScreen;
	rejectReason?: string;
	tabletName?: string;
	validFrom?: string;
	validUntil?: string;
	fingerprintUpdated?: boolean;
	typeAccess?: TTypeAccess;
}

// Request: POST /api/v1/device/client/mobile/register
export interface IDeviceMobileRegisterRequest {
	loginId: string;
	password: string;
	fingerprint: string;
	userAgent: string;
}

// Response: POST /api/v1/device/client/mobile/register
export interface IDeviceMobileRegisterResponse {
	deviceId: number;
	adminName: string;
	account: string;
	osInfo: string;
	browserInfo: string;
	status: TDeviceMobileStatus;
	registDt: string | null;
	tempToken: string;
}

// Request: POST /api/v1/device/client/tablet/verify-register-code
export interface IDeviceTabletVerifyRegisterCodeRequest {
	codeValue: string;
	userAgent: string;
}

// Response: POST /api/v1/device/client/tablet/verify-register-code
export interface IDeviceTabletVerifyRegisterCodeResponse {
	tabletId: number;
	tabletName: string;
	deviceToken: string;
	status: TDeviceTabletStatus;
}

// Request: POST /api/v1/device/client/tablet/verify-remote-code
export interface IDeviceTabletVerifyRemoteCodeRequest {
	codeValue: string;
	userAgent: string;
}

// Response: POST /api/v1/device/client/tablet/verify-remote-code
export interface IDeviceTabletVerifyRemoteCodeResponse {
	tabletId: number;
	tabletName: string;
	deviceToken: string;
	status: TDeviceTabletStatus;
	validFrom?: string;
	validUntil?: string;
}

// Request: POST /api/v1/device/client/login
export interface IDeviceClientLoginRequest {
	loginId: string;
	password: string;
	deviceType: TDeviceType;
	fingerprint?: string;
}

// Response: POST /api/v1/device/client/login
// Spec v4.3.4: FE must save-override both tokens. accessToken = admin session
// JWT (decode for role/email/sessionTimeout); deviceToken = rotated device JWT.
export interface IDeviceClientLoginResponse {
	accessToken: string;
	deviceToken: string;
}
/* ---- DEVICE CLIENT TYPES END---- */
